import {
  getCurrentAccent,
  getCurrentAccentDeep,
  hexToOklch,
  oklchToHex,
} from './utils/seasonalAccent';

// ── Seasonal accent computation (runs once at module load) ────────────────────
const _accent     = getCurrentAccent();
const _accentDeep = getCurrentAccentDeep();
const _base       = hexToOklch(_accent);

// Soft: lighten +0.12L, desaturate 25%
const _accentSoft = oklchToHex({
  L: Math.min(_base.L + 0.12, 1),
  C: _base.C * 0.75,
  h: _base.h,
});

// Gold: partial hue blend toward 85° (yellow), reduce chroma 15%
const _accentGold = oklchToHex({
  L: _base.L,
  C: _base.C * 0.85,
  h: _base.h * 0.6 + 85 * 0.4,
});

// Bright (hover state): slightly darker + more chroma for light-on-dark
const _accentBright = oklchToHex({
  L: Math.max(_base.L - 0.04, 0),
  C: _base.C * 1.08,
  h: _base.h,
});

// Wash values (rgba strings)
const [_r, _g, _b] = [
  parseInt(_accent.slice(1, 3), 16),
  parseInt(_accent.slice(3, 5), 16),
  parseInt(_accent.slice(5, 7), 16),
];
const _accentWash     = `rgba(${_r},${_g},${_b},.10)`;
const _accentWashSoft = `rgba(${_r},${_g},${_b},.07)`;
const _accentSubtle   = `rgba(${_r},${_g},${_b},.10)`;
const _accentBorder   = `rgba(${_r},${_g},${_b},.24)`;

// Light theme: same base hue but adjusted for legibility on light bg
const _accentLight     = _accent;     // same seasonal accent
const _accentDeepLight = _accentDeep; // same seasonal deep
const _accentSoftLight = oklchToHex({
  L: Math.max(_base.L - 0.06, 0),
  C: _base.C * 0.9,
  h: _base.h,
});
const _accentGoldLight = oklchToHex({
  L: Math.max(_base.L - 0.04, 0),
  C: _base.C * 0.85,
  h: _base.h * 0.6 + 85 * 0.4,
});
const [_rl, _gl, _bl] = [_r, _g, _b];
const _accentWashLight     = `rgba(${_rl},${_gl},${_bl},.07)`;
const _accentWashSoftLight = `rgba(${_rl},${_gl},${_bl},.045)`;
const _accentSubtleLight   = `rgba(${_rl},${_gl},${_bl},.09)`;
const _accentBorderLight   = `rgba(${_rl},${_gl},${_bl},.25)`;

export default `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@300;400;500&display=swap');

/* ── RESET ── */


*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
/* Eliminate 300ms tap delay on all interactive elements */
button, a, [role="button"], input[type="button"], input[type="submit"], .clickable {
  touch-action: manipulation;
}

/* ── BASE IMPROVEMENTS ── */
/* Hide number input spinners — Mylo uses custom styling */
input[type="number"] { -moz-appearance: textfield; }
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
/* Prevent iOS text size adjustment on orientation change */
html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
/* Consistent tap color across all interactive elements */
a, button, [role="button"], input, select, textarea {
  -webkit-tap-highlight-color: transparent;
}
/* All flex children should shrink properly */
.card > *, .card-inset > *, .card-gold > * { min-width: 0; }

/* ── TOKENS ── */
/* ════════════════════════════════════════════════════════════════════════════
   THEMING SYSTEM
   ─────────────────────────────────────────────────────────────────────────
   Architecture
   ────────────
   1. Semantic tokens (--color-*) are defined per theme and never referenced
      directly in component CSS — only alias tokens are.
   2. Alias tokens (--bg, --ink, --gold, etc.) map semantics to component CSS.
      Components only ever use alias tokens. Adding a new theme = one new
      [data-theme] block below. No component refactoring needed.
   3. Non-color tokens (spacing, type, radius) live in :root and are
      theme-independent — they never change between themes.
   4. Default theme (dark) is applied via :root so it works with zero JS.
      Light mode uses [data-theme="light"]. Future themes add [data-theme="X"].
   5. OS preference is respected via prefers-color-scheme when no explicit
      theme is set (graceful no-JS fallback).

   Contrast guarantees (WCAG AA = 4.5:1 text, 3:1 UI)
   ────────────────────────────────────────────────────
   Dark theme:
     --ink   on --bg   : #eeebe5 / #080808  → 19.8:1  ✓ AAA
     --t2    on --bg   : #7c7a74 / #080808  →  6.8:1  ✓ AA
     --t3    on --bg   : #484643 / #080808  →  3.3:1  ✓ AA (large text / UI)
     --gold  on --bg   : #b8944a / #080808  →  5.1:1  ✓ AA
     --gold2 on --bg   : #d4af6e / #080808  →  7.1:1  ✓ AAA
     --grn   on --bg   : #4a9e74 / #080808  →  5.4:1  ✓ AA
     --red   on --bg   : #c46060 / #080808  →  4.8:1  ✓ AA
     --on-gold on --gold: #080808 / #b8944a →  5.1:1  ✓ AA

   Light theme:
     --ink   on --bg   : #111111 / #f7f5f1  → 17.1:1  ✓ AAA
     --t2    on --bg   : #4a4844 / #f7f5f1  →  7.3:1  ✓ AAA
     --t3    on --bg   : #7c7a74 / #f7f5f1  →  4.6:1  ✓ AA
     --gold  on --bg   : #8a6a28 / #f7f5f1  →  5.2:1  ✓ AA
     --gold2 on --bg   : #6e5010 / #f7f5f1  →  7.8:1  ✓ AAA
     --grn   on --bg   : #2a7a54 / #f7f5f1  →  5.5:1  ✓ AA
     --red   on --bg   : #a03030 / #f7f5f1  →  6.3:1  ✓ AA
     --on-gold on --gold: #f7f5f1 / #8a6a28 →  5.2:1  ✓ AA
   ════════════════════════════════════════════════════════════════════════════ */

/* ── Non-color tokens — theme-independent ────────────────────────────────── */
:root {
  /* Radius scale */
  --r:    6px;
  --r2:   8px;
  --r3:  12px;
  --r4:  16px;
  --pill: 20px;

  /* Spacing — 8pt grid */
  --sp1:   4px;
  --sp2:   8px;
  --sp3:  12px;
  --sp4:  16px;
  --sp5:  20px;   /* legacy alias — use sp4/sp6 in new code */
  --sp6:  24px;
  --sp7:  28px;   /* legacy alias */
  --sp8:  32px;
  --sp10: 40px;   /* legacy alias */
  --sp12: 48px;
  --sp-section: var(--sp6);   /* 24px — gap between cards */
  --sp-row:     var(--sp3);   /* 12px — gap between rows within a card */
  --page-wash:  rgba(${_r},${_g},${_b},.028); /* desktop ambient warmth — seasonal accent */

  /* Component sizing */
  --input-h:  44px;
  --btn-h:    40px;
  --btn-sm-h: 36px;
  --row-h:    44px;
  --tap:      44px;

  /* Layout */
  --page-px:    var(--sp4);
  --page-py:    var(--sp6);
  --page-max:   1080px;
  --section-gap:var(--sp5);
  --card-p:     var(--sp4);
  --card-gap:   var(--sp4);

  /* Typography */
  --sans: 'Inter', system-ui, -apple-system, sans-serif;
  --display: 'Inter', system-ui, -apple-system, sans-serif;
  --mono: 'JetBrains Mono', monospace;

  /* Type scale */
  --fs-xs:    11px;
  --fs-sm:    13px;
  --fs-base:  13px;   /* alias — use fs-sm */
  --fs-md:    15px;
  --fs-title: 17px;
  --fs-lg:    20px;
  --fs-xl:    26px;

  /* Line height */
  --lh-tight: 1.2;
  --lh-base:  1.5;
  --lh-loose: 1.65;

  /* Letter spacing */
  --ls-tight: -.5px;
  --ls-body:  -.2px;
  --ls-label: .06em;
  --ls-cap:   .10em;
  --ls-wide:  .14em;

}

/* ── Dark theme (default — no JS required) ───────────────────────────────── */
:root,
/* ── Global transition tokens ── */
:root {
  --t-fast:   120ms cubic-bezier(.4,0,.2,1);
  --t-base:   200ms cubic-bezier(.4,0,.2,1);
  --t-slow:   300ms cubic-bezier(.4,0,.2,1);
  --t-spring: 200ms cubic-bezier(.34,1.56,.64,1);
  --ease-out: cubic-bezier(.0,0,.2,1);
  --ease-in-out: cubic-bezier(.4,0,.2,1);
}

[data-theme="dark"] {
  color-scheme: dark;

  /* ── Surface palette ── */
  --color-base:     #0D0B09;   /* page background */
  --color-raised:   #141210;   /* slightly raised surface */
  --color-raised-alt: #181512; /* rhythmic alt — +3 luminance, same hue */
  --color-overlay:  #1C1916;   /* cards, modals */
  --color-sunken:   #221E1A;   /* inset areas, code blocks */

  /* ── Border palette ── */
  --color-border-subtle:  #272320;   /* hairline dividers */
  --color-border:         #332E29;   /* standard borders */
  --color-border-strong:  #42382F;   /* emphasized borders */

  /* ── Text palette ── */
  --color-text-primary:   #eeebe5;   /* headings, labels      19.8:1 on base */
  --color-text-secondary: #8F8880;   /* body, descriptions     ~6.1:1 on base */
  --color-text-tertiary:  #5A5550;   /* captions, placeholders ~4.2:1 on base */
  --color-text-disabled:  #3A3530;   /* disabled / ghost       1.9:1 on base (decorative only) */

  /* ── Accent family — seasonal rotation ── */
  --color-accent:         ${_accent};
  --color-accent-bright:  ${_accentBright};
  --color-accent-subtle:  ${_accentSubtle};
  --color-accent-border:  ${_accentBorder};
  --color-on-accent:      #080808;   /* text on accent bg — stays static */
  --color-accent-soft:    ${_accentSoft};
  --color-accent-deep:    ${_accentDeep};
  --color-accent-gold:    ${_accentGold};
  --color-accent-wash:    ${_accentWash};
  --color-accent-wash-soft: ${_accentWashSoft};

  /* ── Semantic — success (green) ── */
  --color-success:        #4a9e74;   /* 5.4:1 on base */
  --color-success-subtle: rgba(74,158,116,.07);
  --color-success-border: rgba(74,158,116,.15);
  --color-on-success:     #080808;

  /* ── Semantic — danger (red) ── */
  --color-danger:         #c46060;   /* 4.8:1 on base */
  --color-danger-subtle:  rgba(196,96,96,.07);
  --color-danger-border:  rgba(196,96,96,.15);
  --color-on-danger:      #080808;

  /* ── Semantic — info (blue) ── */
  --color-info:           #4a78b8;   /* 4.6:1 on base */
  --color-info-subtle:    rgba(74,120,184,.07);
  --color-info-border:    rgba(74,120,184,.15);
  --color-on-info:        #080808;
}

/* ── Light theme ─────────────────────────────────────────────────────────── */
[data-theme="light"] {
  color-scheme: light;

  --color-base:     #f7f5f1;
  --color-raised:   #f0ede8;
  --color-raised-alt: #e8e3db; /* perceptibly warmer/deeper than raised */
  --color-overlay:  #e8e4dd;
  --color-sunken:   #ede9e2;

  --color-border-subtle:  #dbd6ce;
  --color-border:         #c8bfb0;   /* warmer, more defined card boundary */
  --color-border-strong:  #b0a899;   /* bumped contrast */

  --color-text-primary:   #111111;   /* 17.1:1 on base */
  --color-text-secondary: #4a4844;   /*  7.3:1 on base */
  --color-text-tertiary:  #7c7a74;   /*  4.6:1 on base */
  --color-text-disabled:  #a8a49e;   /*  2.3:1 on base (decorative only) */

  --color-accent:         ${_accentLight};
  --color-accent-bright:  ${_accentSoftLight};
  --color-accent-subtle:  ${_accentSubtleLight};
  --color-accent-border:  ${_accentBorderLight};
  --color-on-accent:      #f7f5f1;
  --color-accent-soft:    ${_accentSoftLight};
  --color-accent-deep:    ${_accentDeepLight};
  --color-accent-gold:    ${_accentGoldLight};
  --color-accent-wash:    ${_accentWashLight};
  --color-accent-wash-soft: ${_accentWashSoftLight};

  --color-success:        #2a7a54;   /* 5.5:1 on base */
  --color-success-subtle: rgba(42,122,84,.08);
  --color-success-border: rgba(42,122,84,.20);
  --color-on-success:     #f7f5f1;

  --color-danger:         #a03030;   /* 6.3:1 on base */
  --color-danger-subtle:  rgba(160,48,48,.08);
  --color-danger-border:  rgba(160,48,48,.20);
  --color-on-danger:      #f7f5f1;

  --color-info:           #2a5a9a;   /* 5.8:1 on base */
  --color-info-subtle:    rgba(42,90,154,.08);
  --color-info-border:    rgba(42,90,154,.20);
  --color-on-info:        #f7f5f1;
}

/* ── OS preference fallback — no JS needed ───────────────────────────────── */
@media (prefers-color-scheme: light) {
  :root:not([data-theme]) {
    color-scheme: light;

    --color-base:     #f7f5f1;
    --color-raised:   #f0ede8;
    --color-raised-alt: #e8e3db;

    --color-border-subtle:  #dbd6ce;
    --color-border:         #c8bfb0;
    --color-border-strong:  #b0a899;

    --color-text-primary:   #111111;
    --color-text-secondary: #4a4844;
    --color-text-tertiary:  #7c7a74;
    --color-text-disabled:  #a8a49e;

    --color-accent:         #A85638;
    --color-accent-bright:  #8C3E24;
    --color-accent-subtle:  rgba(168,86,56,.09);
    --color-accent-border:  rgba(168,86,56,.25);
    --color-on-accent:      #f7f5f1;
    --color-accent-soft:    #C47848;
    --color-accent-deep:    #8A3828;
    --color-accent-gold:    #9C7840;
    --color-accent-wash:    rgba(168,86,56,.07);
    --color-accent-wash-soft: rgba(168,86,56,.045);

    --color-success:        #2a7a54;
    --color-success-subtle: rgba(42,122,84,.08);
    --color-success-border: rgba(42,122,84,.20);
    --color-on-success:     #f7f5f1;

    --color-danger:         #a03030;
    --color-danger-subtle:  rgba(160,48,48,.08);
    --color-danger-border:  rgba(160,48,48,.20);
    --color-on-danger:      #f7f5f1;

    --color-info:           #2a5a9a;
    --color-info-subtle:    rgba(42,90,154,.08);
    --color-info-border:    rgba(42,90,154,.20);
    --color-on-info:        #f7f5f1;
  }
}

/* ── Alias tokens — what components actually use ─────────────────────────── */
/* These never change. A new theme only needs to set --color-* above.        */
:root,
[data-theme] {
  /* Surfaces */
  --bg: var(--color-base);
  --s1: var(--color-raised);
  --s1-alt: var(--color-raised-alt);
  --surface-raised-alt: var(--color-raised-alt);
  --s2: var(--color-overlay);
  --s3: var(--color-sunken);
  --s4: var(--color-sunken);   /* legacy alias */

  /* Borders */
  --b0: var(--color-border-subtle);
  --b1: var(--color-border-subtle);
  --b2: var(--color-border);
  --b3: var(--color-border-strong);

  /* Text */
  --ink: var(--color-text-primary);
  --t2:  var(--color-text-secondary);
  --t3:  var(--color-text-tertiary);
  --t4:  var(--color-text-disabled);

  /* Accent — gold */
  --gold:    var(--color-accent);
  --gold2:   var(--color-accent-bright);
  --goldbg:  var(--color-accent-subtle);
  --goldbr:  var(--color-accent-border);
  --on-gold: var(--color-on-accent);
  /* Accent — auburn (canonical names for migrated components) */
  --accent:    var(--color-accent);
  --on-accent: var(--color-on-accent);
  --accent-s:  var(--color-accent-soft);      /* soft/decorative warmth */
  --accent-d:  var(--color-accent-deep);      /* rust/attention/danger  */
  --accent-g:  var(--color-accent-gold);      /* finance-positive only  */
  --accent-w:  var(--color-accent-wash);      /* ember surface tint     */
  --accent-ws: var(--color-accent-wash-soft); /* honey surface tint     */

  /* Legacy accent aliases */
  --cu:   var(--color-accent);
  --cu2:  var(--color-accent-bright);
  --cubg: var(--color-accent-subtle);
  --cubr: var(--color-accent-border);

  /* Success */
  --grn:   var(--color-success);
  --grnbg: var(--color-success-subtle);
  --grnbr: var(--color-success-border);
  --on-grn:var(--color-on-success);

  /* Danger */
  --red:   var(--color-danger);
  --redbg: var(--color-danger-subtle);
  --redbr: var(--color-danger-border);

  /* Info */
  --blu:   var(--color-info);
  --blubg: var(--color-info-subtle);
}


/* ── Transition scale ── */
:root {
  --t-micro: .1s ease;    /* button active, icon hover */
  --t-fast:  .15s ease;   /* card hover, input focus  */
  --t-mid:   .22s ease;   /* panel open, tab switch   */
  --t-slow:  .3s cubic-bezier(.4,0,.2,1); /* drawer, modal */
}

html, body { height: 100%; background: var(--bg); }
a, button, [role="button"] { -webkit-tap-highlight-color: transparent; }

body {
  font-family: var(--sans);
  font-size: 15px;
  line-height: 1.5;
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  font-feature-settings: "kern" 1, "liga" 1;
  /* Prevent body scroll — app shell owns scrolling */
  overflow: hidden;
  overscroll-behavior: none;
}

/* ── SCROLLBARS ── */
::-webkit-scrollbar { width: 2px; height: 2px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--b1); border-radius: 1px; }
* { scrollbar-width: thin; scrollbar-color: var(--b1) transparent; }

/* ════════════════════════════════════════════
   APP SHELL — responsive
════════════════════════════════════════════ */
/* ── Text utility classes ── */
.text-muted   { color: var(--t2); }
.text-subtle  { color: var(--t3); }
.text-gold    { color: var(--gold2); }
.text-grn     { color: var(--grn); }
.text-red     { color: var(--red); }
.text-mono    { font-family: var(--mono); }
.text-xs      { font-size: var(--fs-xs); }
.text-sm      { font-size: var(--fs-sm); }
.text-base    { font-size: var(--fs-base); }
.text-md      { font-size: var(--fs-md); }
.text-upper   { text-transform: uppercase; letter-spacing: var(--ls-cap); }
.text-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lh-tight     { line-height: var(--lh-tight); }
.lh-loose     { line-height: var(--lh-loose); }

/* ── Layout utilities ── */
.flex         { display: flex; }
.flex-col     { display: flex; flex-direction: column; }
.items-center { align-items: center; }
.justify-between { justify-content: space-between; }
.gap-1        { gap: var(--sp1); }
.gap-2        { gap: var(--sp2); }
.gap-3        { gap: var(--sp3); }
.gap-4        { gap: var(--sp4); }
.w-full       { width: 100%; }
.min-w-0      { min-width: 0; }
.flex-1       { flex: 1; }
.flex-shrink-0 { flex-shrink: 0; }
.text-right   { text-align: right; }
.text-center  { text-align: center; }

.app {
  display: flex;
  height: 100vh;        /* fallback */
  height: 100dvh;       /* modern: excludes iOS browser chrome */
  overflow: hidden;
  position: fixed;      /* prevent iOS bounce-scroll on the shell */
  inset: 0;
}

/* ── SIDEBAR — desktop ── */
.sb {
  width: 240px;
  min-width: 240px;
  background: var(--s1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
  transition: transform .25s cubic-bezier(.4,0,.2,1);
  z-index: 200;
}
.sb-logo {
  padding: var(--sp6) var(--sp5) var(--sp5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;
}
.sb-wordmark { font-family: var(--sans); font-size: 20px; font-weight: 700; letter-spacing: -.6px; color: var(--ink); line-height: 1; }
.sb-wordmark-dot { color: var(--accent); }
.sb-nav {
  padding: var(--sp2) var(--sp2);
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--b2) transparent;
}
.sb-nav::-webkit-scrollbar { width: 6px; }
.sb-nav::-webkit-scrollbar-track { background: transparent; }
.sb-nav::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 3px; }
.sb-nav::-webkit-scrollbar-thumb:hover { background: var(--b3); }
.sb-group-label { font-size: var(--fs-xs); text-transform: uppercase; letter-spacing: .2em; color: var(--t4); padding: var(--sp3) 20px var(--sp1); font-family: var(--mono); line-height: 1; }

/* Nav items — desktop. Active state: accent-soft wash + accent text + accent icon. No vertical bar. */
.ni {
  display: flex;
  align-items: center;
  gap: var(--sp3);
  padding: 0 var(--sp3);
  height: 40px;
  margin-bottom: 1px;
  border-radius: var(--r2);
  cursor: pointer;
  color: var(--t2);
  font-size: 13px;
  font-weight: 400;
  transition: color .12s, background .12s;
  user-select: none;
  letter-spacing: -.1px;
  -webkit-tap-highlight-color: transparent;
}
.ni:hover { color: var(--ink); background: var(--s2); }
.ni.on {
  color: var(--gold);
  background: var(--accent-w);
  font-weight: 500;
  position: relative;
  box-shadow: inset 2px 0 0 0 var(--gold);
}
.ni-ic { font-size: 14px; width: 18px; text-align: center; flex-shrink: 0; color: var(--t3); transition: color .12s; }
.ni:hover .ni-ic { color: var(--t2); }
.ni.on .ni-ic { color: var(--gold); }
.ni-lbl { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ni-badge {
  margin-left: auto;
  background: var(--accent-d);
  color: var(--on-gold);
  font-size: var(--fs-xs);
  padding: 1px 6px;
  border-radius: var(--pill);
  font-family: var(--mono);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  line-height: 1.4;
}
.ni-secondary { color: var(--t3); }
.ni-secondary .ni-ic { color: var(--t4); }
.ni-secondary:hover { color: var(--t2); }
.ni-secondary:hover .ni-ic { color: var(--t3); }

.sb-divider {
  height: 1px;
  background: var(--b1);
  margin: var(--sp3) var(--sp3);
}
.sb-foot { padding: var(--sp3) var(--sp5); border-top: 1px solid var(--b1); font-family: var(--mono); font-size: var(--fs-xs); color: var(--t3); flex-shrink: 0; }
.sb-foot-warn { color: var(--gold); margin-top: var(--sp1); }

/* ── MOBILE HEADER BAR ── */
.mob-header {
  display: none;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  height: calc(48px + env(safe-area-inset-top));
  padding: env(safe-area-inset-top) var(--sp4) 0;
  background: var(--s1);
  border-bottom: 1px solid var(--b1);
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 100;
}
.mob-logo { font-size: 18px; font-weight: 700; letter-spacing: -.6px; color: var(--ink); }
.mob-logo-dot { color: var(--accent); }
.mob-menu-btn {
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--r);
  color: var(--t2);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
  transition: background .1s;
}
.mob-menu-btn:hover, .mob-menu-btn:active { background: var(--s2); }
.mob-header-spacer { width: 44px; flex-shrink: 0; }
.mob-qc-btn {
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  background: var(--goldbg);
  border: 1px solid var(--goldbr);
  border-radius: var(--r2);
  color: var(--gold2);
  font-size: 18px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

/* ── MOBILE DRAWER OVERLAY ── */
.mob-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.6);
  z-index: 199;
  backdrop-filter: blur(4px);
  animation: fadeIn .2s ease;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

/* ── BOTTOM NAV — mobile ── */
.bot-nav {
  display: none;
  position: fixed;
  bottom: 0; left: 0; right: 0;
  background: var(--s1);
  border-top: 1px solid var(--b1);
  z-index: 150;
  /* Safe area for iPhone home indicator */
  padding-bottom: env(safe-area-inset-bottom);
}
.bot-nav-inner {
  display: flex;
  align-items: stretch;
  height: 50px;
}
/* Each nav item: equal flex, large tap target */
.bot-ni {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp1);
  flex: 1;
  cursor: pointer;
  color: var(--t4);
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  transition: color var(--t-fast);
  position: relative;
  min-width: 0;
  padding: var(--sp2) var(--sp1);
  user-select: none;
}
/* Active state: gold color + top accent line */
.bot-ni.on { color: var(--accent); }

/* Center "Mylo" tab — pill button */






/* Icon container: consistent size */
.bot-ni-ic {
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  transition: transform var(--t-fast), color var(--t-fast);
}
.bot-ni:not(.is-fab):active .bot-ni-ic { transform: scale(.94); }
/* Label: slightly larger, clearer */
.bot-ni-lbl {
  font-size: var(--fs-xs);
  font-family: var(--sans);
  letter-spacing: .01em;
  white-space: nowrap;
  line-height: 1;
  transition: color var(--t-fast);
  font-weight: 500;
}
/* Notification dot */
.bot-ni-dot {
  position: absolute;
  top: 9px;
  right: calc(50% - 16px);
  width: 7px; height: 7px;
  border-radius: 50%;
  background: var(--red);
  border: 2px solid var(--s1);
  pointer-events: none;
}

/* ── MAIN CONTENT ── */
.main {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--bg);
  background-image: radial-gradient(ellipse 100% 50% at 50% 0%, var(--page-wash) 0%, transparent 60%);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  scroll-padding-top: var(--sp4);
}
/* When chat is active, main must NOT scroll — chat-msgs owns the scroll */
.main.is-chat { overflow: hidden; }
/* Calendar fills its own shell — main must not also scroll */
.main.is-calendar { overflow: hidden; }

/* Standard page padding */
.page {
  padding: var(--page-py) var(--page-px) var(--sp8);
  max-width: var(--page-max);
  width: 100%;
  animation: pageIn 160ms cubic-bezier(.0,0,.2,1) both;
  box-sizing: border-box;
  /* Center on very wide screens */
  margin-left: auto;
  margin-right: auto;
}
.page > * { scroll-margin-top: var(--sp4); }
@keyframes pageIn {
  from { opacity: 0; transform: translateY(2px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ════════════════════════════════════════════
   RESPONSIVE BREAKPOINTS
════════════════════════════════════════════ */

/* ── Tablet (≤ 900px) ── */
@media (max-width: 900px) {
  .sb { width: 180px; min-width: 180px; }
  :root { --page-px: var(--sp6); --page-py: var(--sp6); }
  .g3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--sp3); }
  .g4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--sp3); }
}
/* ── Tablet narrow (≤ 768px) — start wrapping headers ── */
@media (max-width: 768px) {
  }

/* ── Mobile (≤ 680px) ── */
@media (max-width: 680px) {

  /* ── Shell ── */
  .sb { position:fixed; top:0; left:0; bottom:0; transform:translateX(-100%); width:75vw; max-width:280px; z-index:200; box-shadow:4px 0 32px rgba(0,0,0,.6); transition:transform .25s cubic-bezier(.4,0,.2,1); }
  .sb.open { transform:translateX(0); }
  .sb-foot  { display:none; }
  .mob-header { display:flex; }
  .bot-nav    { display:block; }
  .fab        { display:none !important; }
  .app        { flex-direction:column; }
  /* main fills the viewport minus the fixed bottom nav */
  .main {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    /* Reserve space so fixed bot-nav never covers content */
    padding-bottom: calc(56px + env(safe-area-inset-bottom));
  }
  /* Chat and calendar own their scroll — override the padding-bottom */
  .main.is-chat,
  .main.is-calendar {
    overflow: hidden;
    padding-bottom: 0;
  }

  /* ══ SPACING TOKENS — mobile scale ══ */
  :root {
    --page-px:    var(--sp4);      /* 16px horizontal page padding */
    --page-py:    var(--sp4);      /* 16px top page padding */
    --page-max:   100%;
    --card-p:     var(--sp3);      /* 12px card padding mobile */
    --card-gap:   var(--sp4);      /* 8px between sibling cards mobile */
    --section-gap:var(--sp4);      /* 16px between major sections */
    --input-h:  44px;
    --btn-h:      44px;
    --btn-sm-h:   40px;
    --row-h:      52px;
  }

  /* ══ PAGE ══ */
  .page {
    padding: var(--page-py) var(--page-px) calc(var(--sp8) + 8px);
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
  }
  /* Ensure all cards respect the page boundary */
  .card, .card-inset, .card-gold, .card-border {
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: visible;
  }

  /* ══ TYPOGRAPHY ══ */
  body           { font-size: 15px; }
  .eyebrow       { font-size: var(--fs-xs); }
    /* section-label inherits from base */

  /* ══ GRIDS — single column ══ */
  .g2, .g3, .g4 { grid-template-columns: 1fr !important; gap: var(--sp3) !important; }
  .stat-strip    { grid-template-columns: 1fr 1fr !important; }
  .stat-cell     { padding: var(--card-p); }

  /* ══ FORMS — iOS-safe sizes ══ */
  .field                              { margin-bottom: var(--sp4); }
  .field label                        { font-size: var(--fs-sm); margin-bottom: var(--sp2); }
  .field input,
  .field select,
  .field textarea                     { height: var(--input-h); font-size: 16px !important; padding: 0 var(--sp4); border-radius: var(--r3); }
  .field textarea                     { height: auto; min-height: 100px; padding: var(--sp3) var(--sp4); }
  /* Date inputs need special treatment on iOS — force consistent height and appearance */
  .field input[type="date"]           { height: var(--input-h); -webkit-appearance: none; appearance: none; padding: 0 var(--sp4); line-height: var(--input-h); min-height: var(--input-h); box-sizing: border-box; }
  /* Global 16px rule — prevents iOS zoom on input focus */
  input, select, textarea             { font-size: 16px !important; }

  /* ══ BUTTONS — 44px min tap target ══ */
  .btn    { height: var(--btn-h);    padding: 0 var(--sp5); font-size: 14px; border-radius: var(--r2); }
  .btn-sm { height: var(--btn-sm-h); padding: 0 var(--sp4); font-size: var(--fs-base); }

  /* ══ CARDS ══ */
  .card, .card-inset, .card-gold { border-radius: var(--r3); }
  .card-gold { margin-bottom: var(--card-gap); }
  .card-border { border-radius: var(--r3); overflow-x: auto; -webkit-overflow-scrolling: touch; }


  /* ══ PAGE HEADER ══ */
  .page-hdr {
    flex-wrap: wrap;
    gap: var(--sp2);
    margin-bottom: var(--sp4);
    align-items: flex-start;
  }
  .page-hdr-left { width: 100%; flex: none; }
  .page-hdr-actions {
    width: 100%;
    margin-left: 0;
    display: flex;
    gap: var(--sp2);
  }
  /* All buttons in mobile action bar fill available width equally */
  .page-hdr-actions > * { flex: 1; min-width: 0; }
  .page-hdr-actions .btn,
  .page-hdr-actions .btn-sm,
  .page-hdr-actions .btn-gold {
    flex: 1;
    min-width: 0;
    width: 100%;
    justify-content: center;
    text-align: center;
  }

  /* ══ SIDEBAR NAV DRAWER ══ */
  .ni     { height: 56px; font-size: 15px; padding: 0 var(--sp5); }
  .ni-ic  { font-size: 16px; width: 20px; }

  /* ══ TABLES — horizontal scroll ══ */
  .card-border { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .tbl         { min-width: 360px; }
  .tbl td, .tbl th { font-size: var(--fs-base); padding: var(--sp3) var(--sp2); }

  /* ══ LIST & TASK ROWS ══ */
  .list-row  { min-height: 44px; padding: var(--sp2) var(--sp4); }
  .task-row  { min-height: 44px; padding: var(--sp2) var(--sp4); }
  .task-text { font-size: 15px; }
  .chk       { width: 24px; height: 24px; border-radius: var(--r); flex-shrink: 0; }

  /* ══ CALENDAR ══
     Height = full dvh minus: mob-header (52px + top safe area) + bot-nav (56px + bottom safe area) */
  .cal-shell {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: calc(100dvh - 52px - env(safe-area-inset-top) - 56px - env(safe-area-inset-bottom));
    max-height: calc(100dvh - 52px - env(safe-area-inset-top) - 56px - env(safe-area-inset-bottom));
  }

  /* ══ TAB STRIP — mobile ══ */
  .tab-strip {
  display: flex;
  border: 1px solid var(--b2);
  border-radius: var(--r2);
  overflow: hidden;
  margin-bottom: var(--sp4);
}
.tab-strip-btn {
  flex: 1;
  height: var(--btn-sm-h);
  background: transparent;
  border: none;
  border-left: 1px solid var(--b2);
  color: var(--t3);
  font-size: var(--fs-sm);
  font-family: var(--sans);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp1);
  transition: background var(--t-fast), color var(--t-fast);
  -webkit-tap-highlight-color: transparent;
  white-space: nowrap;
  padding: 0 var(--sp3);
}
.tab-strip-btn:first-child { border-left: none; }
.tab-strip-btn.on,
.tab-strip-btn.is-active {
  background: var(--accent-w);
  color: var(--accent);
  font-weight: 600;
  border-color: var(--accent);
}
  /* tab-strip-btn defined above */

  /* ══ CHAT ══ */
  .chat-shell {
    overflow: hidden;
    /* Push the entire chat shell up so the fixed bot-nav doesn't cover the input */
    padding-bottom: calc(56px + env(safe-area-inset-bottom));
  }
  .chat-top  { display: none; }
  .chat-msgs {
    padding: var(--sp4) var(--sp4) var(--sp3);
    gap: var(--sp4);
  }
  .chat-bot {
    padding: var(--sp3) var(--sp4) var(--sp4);
    background: var(--bg);
    flex-shrink: 0;
    border-top: 1px solid var(--b1);
  }
  .chat-bar  { border-radius: var(--r4); padding: var(--sp3); gap: var(--sp2); }
  .chat-ta   { font-size: 16px !important; }
  .chat-send { width: 46px; height: 46px; font-size: 20px; border-radius: var(--r2); flex-shrink: 0; }
  .chat-voice-btn { width: 46px !important; height: 46px !important; border-radius: var(--r2) !important; }
  .hint-row  { display: none; }
  .chat-disc { display: none; }
  .sugg-row  { overflow-x: auto; flex-wrap: nowrap; padding-bottom: var(--sp1); margin-bottom: var(--sp2); }
  .sugg-chip { white-space: nowrap; flex-shrink: 0; }
  .msg-body  { font-size: var(--fs-md); line-height: 1.65; }

  /* ══ REMINDERS ══ */
  .rm-grid         { grid-template-columns: 1fr; }
  .rm-card         { padding: var(--sp4); }
  .rm-preview-time { font-size: 44px; }

  /* ══ VINYL ══ */
  .disc-lg { width: 52px; height: 52px; }
  .disc-sm { width: 36px; height: 36px; }

  /* ══ QUICK CAPTURE — bottom sheet ══ */
  .qc-box  { width: 100%; border-radius: var(--r4) var(--r4) 0 0; }
  .qc-bg   { padding: 0; align-items: flex-end; }
  .qc-input { font-size: 16px !important; padding: var(--sp5); height: 60px; }
  .qc-chip  { font-size: var(--fs-base); padding: var(--sp2) var(--sp3); height: 38px; }

  /* ══ BADGES ══ */
  .badge { font-size: var(--fs-xs); padding: var(--sp1) var(--sp2); }

  /* ══ TOAST & NOTIFICATIONS — above bottom nav ══ */
  .mem-notify { min-width: 92vw; bottom: calc(64px + env(safe-area-inset-bottom)); border-radius: var(--r3); }
  .toast      { right: var(--sp4); left: var(--sp4); bottom: calc(64px + env(safe-area-inset-bottom)); font-size: var(--fs-base); text-align: center; }

  /* ══ MISC ══ */
  .prog    { height: 4px; }
  .divider { margin: var(--sp5) 0; }
}
/* ── Very small (≤ 380px) ── */
@media (max-width: 380px) {
  :root {
    --page-px:  var(--sp3);
    --card-p:   var(--sp3);
    --fs-xl:    20px;
    --fs-lg:    16px;
  }
  .bot-ni-lbl { font-size: 10px; }
  }

/* ════════════════════════════════════════════
   TYPOGRAPHY — strict hierarchy
════════════════════════════════════════════ */
/* Eyebrow: category label above page title */
.eyebrow {
  font-size: var(--fs-xs);
  letter-spacing: 0.06em;
  color: var(--t3);
  margin-bottom: var(--sp2);
  font-family: var(--sans);
  font-weight: 400;
  line-height: 1;
  text-transform: lowercase;
}
  font-size: var(--fs-xl);
  font-weight: 700;
  letter-spacing: -.6px;
  color: var(--ink);
  letter-spacing: -.6px;
  margin-bottom: var(--sp5);
  line-height: 1.15;
}
.section-label {
  font-size: var(--fs-xs);
  letter-spacing: 0.05em;
  color: var(--t3);
  margin-bottom: var(--sp3);
  font-family: var(--sans);
  font-weight: 400;
  line-height: 1;
  text-transform: lowercase;
}
/* section-label without border — used inline */
.section-label-bare {
  font-size: var(--fs-xs);
  letter-spacing: 0.05em;
  color: var(--t3);
  font-family: var(--sans);
  font-weight: 400;
  margin-bottom: var(--sp2);
  line-height: 1;
  text-transform: lowercase;
}
.divider { height: 1px; background: var(--b1); margin: var(--sp4) 0; }

/* ════════════════════════════════════════════
   PAGE HEADER — consistent across all pages
════════════════════════════════════════════ */
.page-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sp4);
}
.page-hdr-left { flex: 1; min-width: 0; }
.page-hdr-left .page-hdr-actions { display: flex; gap: var(--sp2); flex-shrink: 0; margin-left: var(--sp4); }

/* ════════════════════════════════════════════
   CARDS — three levels of elevation
   All cards: border-radius var(--r3), padding var(--card-p)
   Margin between sibling cards: var(--card-gap)
════════════════════════════════════════════ */
.card {
  background: var(--s2);
  border: 0.5px solid var(--b1);
  border-radius: var(--r3);
  padding: var(--card-p);
  margin-bottom: var(--card-gap);
  transition: border-color var(--t-fast), background var(--t-fast);
}
@media (hover: hover) { .card:hover { border-color: var(--b2); } }

.card-inset {
  background: var(--s2);
  border: 1px solid var(--b1);
  border-radius: var(--r3);
  padding: var(--card-p);
  margin-bottom: var(--card-gap);
  transition: border-color var(--t-fast), background var(--t-fast);
}
@media (hover: hover) { .card-inset:hover { background: var(--s3); border-color: var(--b2); } }

.card-border {
  background: var(--s1);
  border: 1px solid var(--b2);
  border-radius: var(--r3);
  overflow: hidden;
  margin-bottom: var(--card-gap);
  transition: border-color var(--t-fast);
}
@media (hover: hover) { .card-border:hover { border-color: var(--b2); } }

.card-gold {
  background: var(--s1);
  border: 1px solid var(--b1);
  border-top: 2px solid rgba(184,115,51,.5);
  border-radius: var(--r3);
  padding: var(--card-p);
  margin-bottom: var(--sp3);
  transition: border-color var(--t-fast), background var(--t-fast);
}
@media (hover: hover) { .card-gold:hover { background: var(--s2); } }

.card-expandable {
  background: var(--s1);
  border: 1px solid var(--b1);
  border-radius: var(--r3);
  overflow: hidden;
  margin-bottom: var(--sp2);
  transition: border-color var(--t-fast);
}
@media (hover: hover) { .card-expandable:hover { border-color: var(--b2); } }
.card-expand-hdr {
  display: flex;
  align-items: center;
  gap: var(--sp3);
  padding: var(--sp4);
  cursor: pointer;
  transition: background var(--t-fast);
  user-select: none;
}
.card-expand-hdr:hover { background: rgba(255,255,255,.02); }
.card-expand-arrow { font-size: var(--fs-xs); color: var(--t3); margin-left: auto; transition: transform .2s; flex-shrink: 0; }
.card-expand-arrow.open { transform: rotate(90deg); }
.card-expand-body { border-top: 1px solid var(--b1); padding: var(--card-p); animation: expandIn .18s cubic-bezier(.4,0,.2,1); }
@keyframes expandIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }

/* Grid helpers — consistent 12px gap throughout */
.g2 { display: grid; grid-template-columns: 1fr 1fr; gap: var(--sp3); }
.g3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--sp3); }
@media (max-width: 860px) and (min-width: 680px) { .g3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--sp3); } }
.g4 { display: grid; grid-template-columns: repeat(4,1fr); gap: var(--sp3); }

/* ════════════════════════════════════════════
   STAT STRIP
════════════════════════════════════════════ */
.stat-strip {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: var(--sp4);
}
@media (min-width: 480px) {
  .stat-strip { grid-template-columns: repeat(4, 1fr); }
}
.stat-cell {
  background: var(--s1);
  padding: var(--sp3) var(--card-p);
  border-radius: var(--r3);
  border: 0.5px solid var(--b1);
  transition: background var(--t-fast);
}
@media (hover: hover) { .stat-cell:hover { background: var(--s2); } }
.stat-n { font-size: 24px; font-weight: 700; letter-spacing: -.6px; line-height: 1; margin-bottom: var(--sp1); }
.stat-l { font-size: var(--fs-xs); color: var(--t4); text-transform: uppercase; letter-spacing: .14em; font-family: var(--mono); }

/* ════════════════════════════════════════════
   ALERTS / HEADS-UP
════════════════════════════════════════════ */
.hu {
  display: flex;
  gap: var(--sp3);
  padding: var(--sp2) var(--sp4) var(--sp2) var(--sp3);
  border-left: 2px solid var(--gold);
  background: var(--goldbg);
  border-radius: 0 var(--r2) var(--r2) 0;
  margin-bottom: var(--sp2);
  align-items: flex-start;
}
.hu.red  { border-left-color: var(--red);  background: var(--redbg);  }
.hu.grn  { border-left-color: var(--grn);  background: var(--grnbg);  }
.hu-lbl  { font-size: var(--fs-xs); text-transform: uppercase; letter-spacing: .18em; color: var(--gold); margin-bottom: var(--sp1); font-family: var(--mono); }
.hu.red .hu-lbl { color: var(--red); }
.hu.grn .hu-lbl { color: var(--grn); }
.hu-txt  { font-size: var(--fs-base); color: var(--ink); line-height: 1.55; }

/* ════════════════════════════════════════════
   TABLE
════════════════════════════════════════════ */
.tbl { width: 100%; border-collapse: collapse; }
.tbl th {
  text-align: left;
  color: var(--t3);
  font-size: var(--fs-xs);
  letter-spacing: 0.04em;
  padding: 0 var(--sp3) var(--sp2) 0;
  font-family: var(--sans);
  font-weight: 500;
}
.tbl td {
  padding: var(--sp3) var(--sp3) var(--sp3) 0;
  border-top: 1px solid var(--b1);
  font-size: var(--fs-base);
  color: var(--ink);
  vertical-align: middle;
}
.tbl tr:hover td { background: rgba(255,255,255,.015); }

/* ════════════════════════════════════════════
   PROGRESS BAR
════════════════════════════════════════════ */
.prog { height: 5px; background: var(--b2); border-radius: 3px; overflow: hidden; margin-top: var(--sp2); }
.prog-fill { height: 100%; background: var(--grn); border-radius: 2px; transition: width .5s cubic-bezier(.4,0,.2,1); }
/* Goals progress bar — taller, with invisible 44px tap target */
.prog-goal {
  height: 10px;
  background: rgba(255,255,255,.05);
  border-radius: var(--r);
  overflow: hidden;
  cursor: pointer;
  position: relative;
}
.prog-goal::after {
  content: '';
  position: absolute;
  inset: -17px 0;
}

/* ════════════════════════════════════════════
   GOAL CARDS
════════════════════════════════════════════ */
.goal-card {
  border-radius: var(--r3);
  padding: var(--card-p);
  margin-bottom: var(--card-gap);
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  /* Ensure borderLeft 3px doesn't cause layout shift */
  border-left-width: 3px;
}
.goal-card-hdr {
  display: flex;
  align-items: flex-start;
  gap: var(--sp3);
  margin-bottom: var(--sp3);
}
.goal-cat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: var(--sp2);
}
.goal-del-btn {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: var(--t4);
  cursor: pointer;
  font-size: 20px;
  border-radius: var(--r2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: -var(--sp2) -var(--sp2) 0 0;
  -webkit-tap-highlight-color: transparent;
  transition: color var(--t-fast), background var(--t-fast);
}
.goal-update-btn {
  display: block;
  width: 100%;
  margin-top: var(--sp2);
  padding: var(--sp2) var(--sp3);
  font-size: var(--fs-xs);
  color: var(--t3);
  background: transparent;
  border: 1px solid var(--b2);
  border-radius: var(--r);
  cursor: pointer;
  font-family: var(--mono);
  letter-spacing: .06em;
  text-align: center;
  transition: color var(--t-fast), border-color var(--t-fast);
  -webkit-tap-highlight-color: transparent;
}
@media (hover: hover) {
  .goal-update-btn:hover { color: var(--ink); border-color: var(--b3); }
}

/* ════════════════════════════════════════════
   BADGES
════════════════════════════════════════════ */
.badge {
  display: inline-flex;
  align-items: center;
  padding: var(--sp1) var(--sp2);
  border-radius: var(--r);
  font-size: var(--fs-xs);
  letter-spacing: 0.02em;
  font-family: var(--sans);
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.4;
}
.badge-gold { background: var(--goldbg); color: var(--gold2); }
.badge-grn  { background: var(--grnbg);  color: var(--grn);   }
.badge-red  { background: var(--redbg);  color: var(--red);   }
.badge-blu  { background: var(--blubg);  color: var(--blu);   }
.badge-personal { background: rgba(74,120,184,.08); color: var(--blu); }
.badge-dim  { background: rgba(255,255,255,.04); color: var(--t2); }

/* ════════════════════════════════════════════
   DECISIONS
════════════════════════════════════════════ */
.dec { background: var(--s2); border: 1px solid var(--b1); border-top: 2px solid var(--gold); border-radius: var(--r3); padding: var(--card-p); margin-bottom: var(--sp2); transition: border-color var(--t-fast); }
.dec:hover { border-color: var(--b2); }
.dec-tag { font-size: var(--fs-xs); text-transform: uppercase; letter-spacing: .18em; color: var(--gold); margin-bottom: var(--sp2); font-family: var(--mono); opacity: .8; }
.dec-sit { font-size: 12.5px; color: var(--ink); line-height: 1.65; margin-bottom: var(--sp3); }
.dec-opt { display: flex; gap: var(--sp3); padding: var(--sp2) 0; border-bottom: 1px solid var(--b1); font-size: var(--fs-sm); color: var(--t2); }
.dec-opt:last-child { border-bottom: none; }
.dec-opt-n { color: var(--t3); font-size: var(--fs-xs); min-width: 14px; font-family: var(--mono); }
.dec-rec { margin-top: var(--sp3); background: var(--goldbg); border: 1px solid var(--goldbr); border-radius: var(--r2); padding: var(--sp3); font-size: var(--fs-sm); color: var(--ink); line-height: 1.65; }
.dec-rec-lbl { font-size: var(--fs-xs); text-transform: uppercase; letter-spacing: .18em; color: var(--gold); margin-bottom: var(--sp1); font-family: var(--mono); opacity: .8; }

/* ════════════════════════════════════════════
   LIST ROWS
════════════════════════════════════════════ */
.list-row {
  display: flex;
  align-items: center;
  gap: var(--sp3);
  padding: var(--sp3) var(--card-p);
  border-bottom: 1px solid var(--b1);
  transition: background .1s;
  min-height: var(--row-h);
  box-sizing: border-box;
  overflow: hidden;
}
.list-row > * { min-width: 0; }
.list-row:last-child { border-bottom: none; }
@media (hover: hover) { .list-row:hover { background: rgba(255,255,255,.015); } }

/* ════════════════════════════════════════════
   TASKS
════════════════════════════════════════════ */
.task-row {
  display: flex;
  align-items: flex-start;
  gap: var(--sp3);
  padding: var(--sp3) var(--card-p);
  border-bottom: 1px solid var(--b1);
  cursor: pointer;
  transition: background .1s;
  min-height: var(--row-h);
  -webkit-tap-highlight-color: transparent;
}
.task-row:last-child { border-bottom: none; }
@media (hover: hover) { .task-row:hover { background: rgba(255,255,255,.018); } }
/* Show task actions on hover (desktop), always visible on mobile */
.task-actions { opacity: 0; transition: opacity .15s; }
.task-row:hover .task-actions { opacity: 1; }
@media (max-width: 680px) { .task-actions { opacity: 0; max-height: 0; overflow: hidden; transition: opacity .15s, max-height .15s; }
  .task-row.expanded .task-actions { opacity: 1; max-height: 40px; } }

/* ── Touch target expansion — ensures all small icon buttons are 44px tappable ── */
.touch-target {
  position: relative;
}
.touch-target::after {
  content: '';
  position: absolute;
  inset: -8px;
  min-width: 44px;
  min-height: 44px;
}
.chk {
  width: 20px; height: 20px;
  border-radius: var(--r);
  border: 1.5px solid var(--b3);
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: background var(--t-fast), border-color var(--t-fast);
  margin-top: var(--sp1);
}
.chk.checked { background: var(--gold); border-color: var(--gold); }
.chk-mark { font-size: var(--fs-xs); color: #000; font-weight: 800; line-height: 1; }
.task-text { font-size: var(--fs-base); color: var(--ink); flex: 1; line-height: 1.55; word-break: break-word; }
.task-text.done { color: var(--t3); text-decoration: line-through; text-decoration-color: var(--t4); }

/* ════════════════════════════════════════════
   FORMS
════════════════════════════════════════════ */
.field { margin-bottom: var(--sp4); width: 100%; box-sizing: border-box; min-width: 0; }
.field label { display: block; font-size: var(--fs-xs); text-transform: uppercase; letter-spacing: var(--ls-cap); color: var(--t4); margin-bottom: var(--sp2); font-family: var(--mono); font-weight: 500; }
.field input, .field select, .field textarea {
  width: 100%;
  background: var(--s2);
  border: 1px solid var(--b2);
  color: var(--ink);
  font-family: var(--sans);
  font-size: var(--fs-base);
  height: var(--input-h);
  padding: 0 var(--sp3);
  border-radius: var(--r2);
  outline: none;
  transition: border-color .12s, background .12s;
  box-sizing: border-box;
}
.field textarea { height: auto; padding: var(--sp2) var(--sp3); }
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp3);
  margin-bottom: var(--sp4);
}
.field-row .field { margin-bottom: 0; }
.field input:focus, .field select:focus, .field textarea:focus {
  border-color: var(--accent);
  background: var(--s2);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 15%, transparent);
  outline: none;
}
.field select option { background: #141414; }

/* ════════════════════════════════════════════
   BUTTONS
   btn: 32px  btn-sm: 28px
════════════════════════════════════════════ */
/* Global accessible focus ring */
*:focus-visible {
  outline: 2px solid var(--gold);
  outline-offset: 2px;
  border-radius: 4px;
}

/* ── Icon buttons — standardized square tap targets ── */
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--t3);
  border-radius: var(--r2);
  -webkit-tap-highlight-color: transparent;
  transition: color var(--t-fast), background var(--t-fast);
  flex-shrink: 0;
}
.icon-btn:hover { color: var(--ink); }
.icon-btn-md { width: 36px; height: 36px; font-size: 18px; }
.icon-btn-sm { width: 28px; height: 28px; font-size: 14px; }
.icon-btn-lg { width: 44px; height: 44px; font-size: 20px; }
.icon-btn-del:hover { color: var(--red); }
.icon-btn-edit:hover { color: var(--gold2); }

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: var(--btn-h);
  padding: 0 var(--sp4);
  background: transparent;
  border: 1px solid var(--b2);
  color: var(--t3);
  font-family: var(--sans);
  font-size: var(--fs-sm);
  letter-spacing: -.1px;
  border-radius: var(--r2);
  cursor: pointer;
  transition: color .12s, border-color .12s, background .12s;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}
.btn:hover { color: var(--ink); border-color: var(--b3); background: rgba(255,255,255,.025); }
.btn:hover:not(:disabled) { border-color: var(--b3); color: var(--ink); }
.btn:active:not(:disabled) { transform: scale(.97); opacity: .9; transition: transform 60ms; }
.btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(184,115,51,.35); border-color: var(--gold); }
.btn-gold { background: var(--gold); color: var(--bg); border-color: var(--gold); font-weight: 700; letter-spacing: -.1px; }
.btn.primary { background: var(--gold); color: var(--bg); border-color: var(--gold); font-weight: 600; }
.btn-gold:hover { opacity: .88; }
.btn-gold:active { transform: scale(.97); transition: transform 60ms; }
.btn-gold:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
.btn-sm { height: var(--btn-sm-h); padding: 0 var(--sp3); font-size: var(--fs-xs); font-weight: 600; letter-spacing: .01em; }
/* ── Segment control — uniform for all tab strips ── */
/* ── Pill group (connected button strip) ── */
.pill-group {
  display: flex;
  flex-shrink: 0;
}
.pill-group-btn {
  height: var(--btn-sm-h);
  padding: 0 var(--sp3);
  font-size: var(--fs-xs);
  font-family: var(--mono);
  border: 1px solid var(--b2);
  border-left: none;
  background: transparent;
  color: var(--t3);
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--t-fast), color var(--t-fast), border-color var(--t-fast);
  -webkit-tap-highlight-color: transparent;
}
.pill-group-btn:first-child {
  border-left: 1px solid var(--b2);
  border-radius: var(--r) 0 0 var(--r);
}
.pill-group-btn:last-child {
  border-radius: 0 var(--r) var(--r) 0;
}
.pill-group-btn.is-active {
  background: var(--accent-w);
  color: var(--accent);
  border-color: var(--accent);
}
.seg {
  display: flex;
  border: 1px solid var(--b2);
  border-radius: var(--r2);
  overflow: hidden;
  margin-bottom: var(--sp4);
}
.seg-btn {
  flex: 1;
  height: var(--btn-sm-h);
  background: transparent;
  border: none;
  border-left: 1px solid var(--b2);
  color: var(--t3);
  font-size: var(--fs-sm);
  font-family: var(--sans);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp1);
  transition: background var(--t-fast), color var(--t-fast);
  -webkit-tap-highlight-color: transparent;
  white-space: nowrap;
  padding: 0 var(--sp3);
}
.seg-btn:first-child { border-left: none; }
.seg-btn.is-active {
  background: var(--accent-w);
  color: var(--accent);
  font-weight: 600;
}
.seg-btn:hover:not(.is-active) { background: var(--s2); color: var(--t2); }
.seg-badge {
  font-size: var(--fs-xs);
  font-family: var(--mono);
  background: var(--s3);
  padding: var(--sp1) var(--sp2);
  border-radius: var(--r4);
  color: var(--t3);
  line-height: 1;
}
.seg-btn.is-active .seg-badge { background: var(--accent-w); color: var(--accent); }

.btn-ghost { border-color: transparent; }
.btn-ghost:hover { border-color: var(--b2); }

/* ── TAB STRIP — segmented button group ── */
/* Finance summary grid */
.finance-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}
@media (min-width: 480px) {
  .finance-summary-grid { grid-template-columns: repeat(4, 1fr); }
}
.fin-runway-grid { grid-template-columns: 1fr 1fr; }
@media (max-width: 380px) { .fin-runway-grid { grid-template-columns: 1fr; } }
@media (max-width: 680px) {
  .finance-summary-grid { grid-template-columns: 1fr 1fr; gap: var(--sp3) !important; }
}

/* ════════════════════════════════════════════
   BUDGET BARS
════════════════════════════════════════════ */
.bgt-row { margin-bottom: var(--sp3); }
.bgt-meta { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: var(--sp1); }
.bgt-name { font-size: 11.5px; color: var(--ink); text-transform: capitalize; }
.bgt-val  { font-size: var(--fs-xs); color: var(--t2); font-family: var(--mono); }
.bgt-over { font-size: var(--fs-xs); color: var(--red); font-family: var(--mono); }

/* ════════════════════════════════════════════
   MEMORY ROWS
════════════════════════════════════════════ */
.mem-row { display: flex; gap: var(--sp3); padding: var(--sp2) var(--sp3); background: var(--s2); border: 1px solid var(--b1); border-radius: var(--r2); margin-bottom: var(--sp1); align-items: flex-start; transition: border-color .12s; }
.mem-row:hover { border-color: var(--b2); }
.mem-cat { font-size: var(--fs-xs); text-transform: uppercase; letter-spacing: .14em; min-width: 72px; padding-top: var(--sp1); font-family: var(--mono); }
.mem-text { font-size: var(--fs-sm); color: var(--ink); flex: 1; line-height: 1.6; }
.mem-del { color: var(--t4); cursor: pointer; font-size: 14px; transition: color .1s; line-height: 1; }
.mem-del:hover { color: var(--red); }

/* ════════════════════════════════════════════
   MEMORY NOTIFICATION
════════════════════════════════════════════ */
.mem-notify {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--s2);
  border: 0.5px solid var(--b2);
  border-radius: var(--r3);
  padding: var(--sp3) var(--sp4);
  min-width: 300px;
  max-width: 420px;
  z-index: 1000;
  box-shadow: 0 4px 24px rgba(0,0,0,.35);
  animation: memIn .2s cubic-bezier(.4,0,.2,1);
  display: flex;
  align-items: flex-start;
  gap: var(--sp3);
}
@keyframes memIn {
  from { opacity: 0; transform: translateX(-50%) translateY(6px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}
.mem-notify-icon { font-size: var(--fs-base); flex-shrink: 0; margin-top: 2px; color: var(--accent); opacity: .7; }
.mem-notify-body { flex: 1; min-width: 0; }
.mem-notify-label { font-size: var(--fs-xs); color: var(--t3); font-family: var(--sans); margin-bottom: var(--sp1); letter-spacing: 0.02em; }
.mem-notify-msg { font-size: var(--fs-sm); color: var(--ink); line-height: 1.5; }
.mem-notify-actions { display: flex; gap: var(--sp2); margin-top: var(--sp3); }
.mem-notify-confirm { font-size: var(--fs-xs); padding: 6px var(--sp3); border-radius: var(--r2); border: 0.5px solid var(--accent); background: color-mix(in srgb, var(--accent) 10%, transparent); color: var(--accent); cursor: pointer; font-family: var(--sans); transition: background .1s; }
.mem-notify-confirm:hover { background: color-mix(in srgb, var(--accent) 16%, transparent); }
.mem-notify-dismiss { font-size: var(--fs-xs); padding: 6px var(--sp3); border-radius: var(--r2); border: 0.5px solid var(--b2); background: transparent; color: var(--t3); cursor: pointer; font-family: var(--sans); transition: color .1s; }
.mem-notify-dismiss:hover { color: var(--ink); }

/* ════════════════════════════════════════════
   MEMORY PULSE
════════════════════════════════════════════ */
.mem-pulse { display: inline-block; width: 5px; height: 5px; border-radius: 50%; background: var(--gold); margin-left: auto; animation: memPulse 2s infinite; }
@keyframes memPulse { 0%, 100% { opacity: .3; transform: scale(1); } 50% { opacity: 1; transform: scale(1.3); } }

/* ════════════════════════════════════════════
   TIMELINE
════════════════════════════════════════════ */
.tl { border-left: 1px solid var(--b2); margin-left: var(--sp2); padding-left: var(--sp5); }
.tl-row { display: flex; gap: var(--sp4); align-items: flex-start; padding: var(--sp3) 0; position: relative; }
.tl-dot { position: absolute; left: -24px; top: 14px; width: 7px; height: 7px; border-radius: 50%; background: var(--s2); border: 1px solid var(--b3); }
.tl-dot.future { border-color: var(--gold); background: var(--goldbg); }
.tl-date { font-size: var(--fs-xs); color: var(--t3); min-width: 72px; padding-top: var(--sp1); font-family: var(--mono); }
.tl-label { font-size: 12.5px; color: var(--ink); flex: 1; }

/* ════════════════════════════════════════════
   INBOX
════════════════════════════════════════════ */
.ix-row { display: flex; gap: var(--sp3); padding: var(--sp3) var(--card-p); border-bottom: 1px solid var(--b1); transition: background .1s; }
.ix-row:last-child { border-bottom: none; }
.ix-row:hover { background: rgba(255,255,255,.015); }
.ix-from { font-size: var(--fs-xs); color: var(--t3); margin-bottom: var(--sp1); font-family: var(--mono); }
.ix-subj { font-size: 12.5px; color: var(--ink); }
.ix-detail { margin-top: var(--sp1); font-size: var(--fs-xs); font-family: var(--mono); }

/* ════════════════════════════════════════════
   FAB — floating action button
════════════════════════════════════════════ */
/* ════════════════════════════════════════════
   TOAST
════════════════════════════════════════════ */
.toast {
  position: fixed;
  bottom: 80px;
  right: 24px;
  background: var(--s3);
  border: 1px solid var(--b3);
  border-radius: var(--r);
  padding: var(--sp2) var(--sp3);
  font-size: var(--fs-sm);
  color: var(--ink);
  z-index: 999;
  animation: toastIn .16s ease;
  pointer-events: none;
  font-family: var(--mono);
  box-shadow: 0 8px 24px rgba(0,0,0,.7);
}
@keyframes toastIn { from { opacity: 0; transform: translateY(8px) scale(.96); } to { opacity: 1; transform: translateY(0) scale(1); } }

/* ════════════════════════════════════════════
   QUICK CAPTURE
════════════════════════════════════════════ */
.qc-bg { position: fixed; inset: 0; background: rgba(0,0,0,.8); backdrop-filter: blur(16px); z-index: 900; display: flex; align-items: flex-start; justify-content: center; padding-top: var(--sp12); animation: qcIn .14s ease; }
@keyframes qcIn { from { opacity: 0; } to { opacity: 1; } }
.qc-box { background: var(--s1); border: 1px solid var(--b2); border-radius: var(--r3); width: 540px; max-width: 92vw; box-shadow: 0 32px 80px rgba(0,0,0,.95), 0 0 0 1px rgba(255,255,255,.03); overflow: hidden; animation: qcSlide .16s cubic-bezier(.4,0,.2,1); }
@keyframes qcSlide { from { transform: translateY(-8px) scale(.98); } to { transform: translateY(0) scale(1); } }
.qc-input { width: 100%; background: transparent; border: none; outline: none; color: var(--ink); font-family: var(--sans); font-size: 15px; padding: var(--sp4) var(--sp5); border-bottom: 1px solid var(--b2); }
.qc-input::placeholder { color: var(--t3); }
.qc-chips { padding: var(--sp2) var(--sp5); display: flex; flex-wrap: wrap; gap: var(--sp1); }
.qc-chip { font-size: 9.5px; color: var(--t2); border: 1px solid var(--b2); border-radius: var(--pill); padding: var(--sp1) var(--sp2); cursor: pointer; background: transparent; font-family: var(--mono); transition: color .1s, border-color .1s; }
.qc-chip:hover { color: var(--gold2); border-color: var(--gold); }
.qc-result { padding: var(--sp2) var(--sp5) var(--sp3); font-size: var(--fs-xs); color: var(--grn); border-top: 1px solid var(--b1); font-family: var(--mono); }

/* ════════════════════════════════════════════
   CHAT
════════════════════════════════════════════ */
.chat-shell {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  position: relative;
}
.chat-top {
  padding: var(--sp2) var(--sp5);
  border-bottom: 1px solid var(--b1);
  flex-shrink: 0;
  background: var(--s1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
}
.chat-msgs {
  flex: 1;
  overflow-y: auto;
  padding: var(--sp4) var(--sp5) var(--sp5);
  display: flex;
  flex-direction: column;
  gap: var(--sp4);
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  min-height: 0;
}
.chat-bot {
  padding: var(--sp2) var(--sp4);
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  border-top: 1px solid var(--b1);
  flex-shrink: 0;
  background: var(--bg);
}
.msg { display: flex; gap: var(--sp3); align-items: flex-start; padding: var(--sp1) 0; animation: fadeUp .16s ease; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
.msg-av {
  width: 32px; height: 32px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: var(--fs-sm); font-weight: 700;
  flex-shrink: 0; margin-top: var(--sp1);
}
.av-mylo { background: var(--goldbg); border: 1px solid var(--goldbr); color: var(--gold2); }
.av-user { background: var(--s3); border: 1px solid var(--b2); color: var(--t2); }
.msg-meta { font-size: var(--fs-xs); color: var(--t4); margin-bottom: var(--sp1); font-family: var(--mono); text-transform: uppercase; letter-spacing: .14em; }
.msg-body { font-size: var(--fs-md); color: var(--ink); line-height: 1.68; }
.msg-body p { margin-bottom: var(--sp1); }
.msg-body p:last-child { margin-bottom: 0; }
.msg-user .msg-body { color: var(--t2); font-size: 15px; }
.action-pill { display: inline-flex; align-items: center; gap: var(--sp1); background: var(--grnbg); border: 1px solid var(--grnbr); border-radius: var(--pill); padding: var(--sp1) var(--sp2); font-size: var(--fs-xs); color: var(--grn); font-family: var(--mono); margin-top: var(--sp2); }
.sugg-row { display: flex; gap: var(--sp2); flex-wrap: wrap; margin-bottom: var(--sp2); }
.sugg-chip { font-size: var(--fs-sm); color: var(--t2); background: transparent; border: 1px solid var(--b2); border-radius: var(--pill); padding: var(--sp2) var(--sp3); cursor: pointer; font-family: var(--mono); transition: color .1s, border-color .1s; -webkit-tap-highlight-color: transparent; }
.sugg-chip:hover { color: var(--gold2); border-color: var(--gold); }
.hint-row { display: flex; gap: var(--sp2); flex-wrap: wrap; margin-bottom: var(--sp2); overflow-x: auto; padding-bottom: var(--sp1); }
.hint-chip { font-size: var(--fs-sm); color: var(--t3); background: var(--s2); border: 1px solid var(--b1); border-radius: var(--pill); padding: var(--sp2) var(--sp3); cursor: pointer; font-family: var(--mono); transition: color .1s, border-color .1s; white-space: nowrap; flex-shrink: 0; -webkit-tap-highlight-color: transparent; }
.hint-chip:hover, .hint-chip:active { color: var(--gold); border-color: var(--gold); }
.chat-bar {
  display: flex;
  gap: var(--sp2);
  align-items: flex-end;
  background: var(--s2);
  border: 1px solid var(--b2);
  border-radius: var(--r4);
  padding: var(--sp2) var(--sp3);
  transition: border-color .2s, box-shadow .2s;
}
.chat-bar:focus-within {
  border-color: var(--goldbr);
  box-shadow: 0 0 0 3px rgba(184,115,51,.08);
}
.chat-ta {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--ink);
  font-family: var(--sans);
  font-size: 16px;
  resize: none;
  line-height: 1.5;
  min-height: 24px;
  max-height: 140px;
  -webkit-appearance: none;
}
.chat-ta::placeholder { color: var(--t3); }
.chat-send {
  background: var(--gold);
  border: none;
  color: #000;
  width: 40px; height: 40px;
  border-radius: var(--r2);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; font-weight: 700;
  flex-shrink: 0;
  transition: background var(--t-fast);
  -webkit-tap-highlight-color: transparent;
}
.chat-send:active { background: var(--gold2); transform: scale(.95); }
.chat-send:disabled { background: var(--b2); cursor: not-allowed; }
.chat-disc { text-align: center; font-size: var(--fs-xs); color: var(--t4); margin-top: var(--sp2); font-family: var(--mono); }
.tdot { display: inline-block; width: 4px; height: 4px; border-radius: 50%; background: var(--gold); margin: 0 var(--sp1); animation: tdot 1.2s infinite; }
.tdot:nth-child(2) { animation-delay: .2s; }
.tdot:nth-child(3) { animation-delay: .4s; }
@keyframes tdot { 0%,80%,100% { opacity: .2; } 40% { opacity: 1; } }

/* ════════════════════════════════════════════
   VOICE MODAL
════════════════════════════════════════════ */
.voice-bg { position: fixed; inset: 0; background: rgba(0,0,0,.92); backdrop-filter: blur(24px); z-index: 950; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--sp6); animation: qcIn .15s ease; }
.voice-orb { width: 88px; height: 88px; border-radius: 50%; background: var(--goldbg); border: 1px solid var(--gold); display: flex; align-items: center; justify-content: center; font-size: 32px; cursor: pointer; transition: all .2s; }
.voice-orb.listening { background: var(--redbg); border-color: var(--red); animation: orbPulse 1s infinite; }
.voice-orb.processing { animation: orbSpin 2s linear infinite; }
.voice-orb.speaking { background: var(--grnbg); border-color: var(--grn); }
@keyframes orbPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(184,80,80,.25); } 50% { box-shadow: 0 0 0 16px rgba(184,80,80,0); } }
@keyframes orbSpin { from { filter: hue-rotate(0deg); } to { filter: hue-rotate(360deg); } }
.voice-status { font-size: var(--fs-sm); color: var(--t3); font-family: var(--mono); letter-spacing: .1em; }
.voice-tx { max-width: 480px; text-align: center; font-size: 18px; color: var(--ink); font-weight: 600; line-height: 1.3; }
.voice-resp { max-width: 480px; text-align: center; font-size: 14px; color: var(--t2); line-height: 1.65; }
.voice-egs { display: flex; flex-wrap: wrap; gap: var(--sp2); justify-content: center; max-width: 480px; }
.voice-eg { font-size: 9.5px; color: var(--t3); background: transparent; border: 1px solid var(--b2); border-radius: var(--pill); padding: var(--sp1) var(--sp3); font-family: var(--mono); cursor: pointer; transition: color .1s, border-color .1s; }
.voice-eg:hover { color: var(--gold2); border-color: var(--gold); }
.voice-close { background: transparent; border: 1px solid var(--b2); color: var(--t3); font-family: var(--mono); font-size: var(--fs-xs); padding: var(--sp2) var(--sp4); border-radius: var(--r); cursor: pointer; transition: all .1s; }
.voice-close:hover { border-color: var(--red); color: var(--red); }


/* ════════════════════════════════════════════
   CALENDAR — 8px spacing system
   Tokens: --sp1=8  --sp2=16  --sp3=24  --sp4=32
   All sizes are multiples of 8.
════════════════════════════════════════════ */

/* ─── Shell ─────────────────────────────── */
.cal-shell {
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: var(--bg);
}

/* ─── Left sidebar — fixed 216px (27×8) ── */
.cal-sidebar {
  width: 216px;
  min-width: 216px;
  flex-shrink: 0;
  border-right: 1px solid var(--b1);
  background: var(--s1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ─── Mini-month ─────────────────────────── */
.mini-cal {
  padding: var(--sp4) var(--sp4) var(--sp2);   /* 2sp / 2sp / 1sp */
  flex-shrink: 0;
}
.mini-cal-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;             /* 4sp */
  margin-bottom: var(--sp2);       /* 1sp */
}
.mini-cal-title {
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -.1px;
  line-height: 1;
}
.mini-nav-btn {
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  color: var(--t3);
  cursor: pointer;
  font-size: 14px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background .1s, color .1s;
  flex-shrink: 0;
}
.mini-nav-btn:hover { background: rgba(255,255,255,.07); color: var(--ink); }

/* DOW labels — 7 equal columns */
.mini-dow-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  height: 24px;
}
.mini-dow {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-xs);
  color: var(--t3);
  font-family: var(--mono);
  text-transform: uppercase;
  letter-spacing: .05em;
}

/* Day cells — 24×24 circles, no column gap */
.mini-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: var(--sp1);
}
.mini-day {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-size: var(--fs-xs);
  color: var(--t2);
  font-family: var(--mono);
  cursor: pointer;
  transition: background .1s, color .1s;
  position: relative;
  user-select: none;
}
.mini-day:hover { background: rgba(255,255,255,.08); color: var(--ink); }
.mini-day.today { background: var(--gold); color: #000; font-weight: 700; }
.mini-day.sel:not(.today) { background: var(--goldbg); color: var(--gold2); }
.mini-day.dim { opacity: .2; pointer-events: none; }
.mini-day.has-ev::after {
  content: '';
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--gold);
  opacity: .5;
}

/* ─── Day agenda panel ───────────────────── */
.day-agenda {
  flex: 1;
  border-top: 1px solid var(--b1);
  overflow-y: auto;
  padding: var(--sp4);            /* 2sp all sides */
  scrollbar-width: thin;
  scrollbar-color: var(--b2) transparent;
  min-height: 0;
}
.agenda-date {
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--ink);
  margin-bottom: var(--sp4);
  letter-spacing: -.1px;
  line-height: 1;
}
.agenda-sec {
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: .2em;
  color: var(--t3);
  font-family: var(--mono);
  margin-bottom: var(--sp2);
  margin-top: var(--sp4);
  line-height: 1;
}
.agenda-sec:first-of-type { margin-top: 0; }
.agenda-ev {
  display: flex;
  align-items: flex-start;
  gap: var(--sp2);
  padding: var(--sp2) var(--sp2) var(--sp2) var(--sp2); /* extra 2px left to clear the border */
  border-radius: 4px;
  margin-bottom: var(--sp1);
  cursor: pointer;
  border-left: 2px solid transparent;
  transition: background .1s;
}
.agenda-ev:hover { background: rgba(255,255,255,.04); }
.agenda-ev.ev-work   { border-left-color: var(--gold); }
.agenda-ev.ev-health { border-left-color: var(--grn); }
.agenda-ev-time {
  font-size: var(--fs-xs);
  color: var(--t3);
  font-family: var(--mono);
  min-width: 48px;
  flex-shrink: 0;
  line-height: 1.6;
  letter-spacing: .02em;
}
.agenda-ev-title {
  font-size: var(--fs-sm);
  color: var(--ink);
  line-height: 1.5;
}
.agenda-task {
  display: flex;
  gap: var(--sp2);
  padding: var(--sp2) var(--sp2);
  margin-bottom: var(--sp1);
  align-items: flex-start;
}
.agenda-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--gold);
  opacity: .5;
  flex-shrink: 0;
  margin-top: var(--sp1);
}
.agenda-task-text { font-size: var(--fs-sm); color: var(--t2); line-height: 1.5; }
.agenda-empty { font-size: var(--fs-sm); color: var(--t4); padding: var(--sp2); }

/* ─── Main panel ─────────────────────────── */
.cal-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}

/* ─── Toolbar — 48px fixed height ───────── */
.cal-toolbar {
  display: flex;
  align-items: center;
  gap: var(--sp2);
  padding: 0 var(--sp4);
  height: 48px;             /* 6sp */
  flex-shrink: 0;
  background: var(--s1);
  border-bottom: 1px solid var(--b1);
}
.cal-toolbar-title {
  flex: 1;
  font-size: var(--fs-base);
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -.2px;
  line-height: 1;
}

/* All toolbar controls same height */
.cal-btn-today,
.cal-btn-nav,
.cal-vbtn {
  height: 28px;             /* 3.5sp */
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid var(--b2);
  background: transparent;
  cursor: pointer;
  transition: all .12s;
  flex-shrink: 0;
  box-sizing: border-box;
}
.cal-btn-today {
  padding: 0 var(--sp3);
  font-family: var(--mono);
  font-size: var(--fs-xs);
  color: var(--t2);
  letter-spacing: .05em;
  white-space: nowrap;
}
.cal-btn-today:hover { color: var(--gold2); border-color: var(--gold); }
.cal-btn-nav {
  width: 28px;
  color: var(--t3);
  font-size: 14px;
}
.cal-btn-nav:hover { color: var(--ink); border-color: var(--b3); }
.cal-vbtn {
  padding: 0 var(--sp3);
  font-family: var(--mono);
  font-size: var(--fs-xs);
  color: var(--t3);
  letter-spacing: .05em;
  border-radius: 0;
}
.cal-vbtn:first-child { border-radius: 4px 0 0 4px; }
.cal-vbtn:last-child  { border-radius: 0 4px 4px 0; border-left: none; }
.cal-vbtn.on { color: var(--gold2); border-color: var(--gold); background: var(--goldbg); position: relative; z-index: 1; }
.cal-vbtn:hover:not(.on) { color: var(--ink); border-color: var(--b3); }

/* ─── Add-event bar — 48px fixed height ─── */
.cal-add-bar {
  display: flex;
  align-items: center;
  gap: var(--sp2);
  padding: 0 var(--sp4);
  height: 48px;             /* echoes toolbar height — creates visual rhythm */
  flex-shrink: 0;
  background: var(--s2);
  border-bottom: 1px solid var(--b1);
}

/* All inputs share identical height and baseline */
.cal-add-input,
.cal-add-date,
.cal-add-type {
  height: 32px;
  background: var(--s3);
  border: 1px solid var(--b2);
  border-radius: 4px;
  color: var(--ink);
  font-family: var(--mono);
  font-size: var(--fs-sm);
  outline: none;
  padding: 0 var(--sp2);
  transition: border-color .12s;
  box-sizing: border-box;
}
.cal-add-input {
  flex: 1;
  font-family: var(--sans);
  font-size: var(--fs-sm);
  color: var(--ink);
}
.cal-add-input::placeholder { color: var(--t3); }
.cal-add-input:focus  { border-color: var(--gold); }
.cal-add-date:focus   { border-color: var(--gold); color: var(--ink); }
.cal-add-date  { width: 120px; color: var(--t2); }
.cal-add-type  { width: 80px; }
.cal-add-type option { background: #141414; }
.cal-add-btn {
  height: 32px;
  padding: 0 var(--sp4);
  background: var(--gold);
  color: #000;
  border: none;
  border-radius: 4px;
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--t-fast);
  flex-shrink: 0;
  white-space: nowrap;
}
.cal-add-btn:hover { background: var(--gold2); }

/* ════════════════════════════════════════════
   WEEK VIEW
   Single flat 7-column grid — headers in row 1,
   body cells in row 2. One grid = perfect alignment.
════════════════════════════════════════════ */
.week-view {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/*
  The grid uses auto for the header row and 1fr for
  the body row, so body cells always fill remaining space.
  We cannot use sticky inside a CSS grid row easily,
  so the header strip is extracted as a separate
  fixed-height row, and the body scrolls below it.
*/
.week-hdr-strip {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  flex-shrink: 0;
  background: var(--s1);
  border-bottom: 2px solid var(--b1);
}

.week-body-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: var(--b2) transparent;
}

.week-body-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  min-height: 100%;          /* fills scroll area */
  align-items: stretch;
}

/* Shared border rule for both header and body columns */
.week-col-hdr { border-right: 1px solid var(--b1); }
.week-col-hdr:last-child { border-right: none; }
.week-col-body { border-right: 1px solid var(--b1); }
.week-col-body:last-child { border-right: none; }

/* ── Header cells ── */
.week-col-hdr {
  padding: var(--sp4) var(--sp2);        /* 2sp vertical, 1sp horizontal */
  text-align: center;
  cursor: pointer;
  transition: background var(--t-fast);
  box-sizing: border-box;
  min-width: 0;
}
.week-col-hdr:hover { background: var(--s2); }
.week-col-hdr.is-today { background: var(--goldbg); }

.week-col-name {
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: .14em;
  color: var(--t3);
  font-family: var(--mono);
  margin-bottom: var(--sp2);       /* 1sp */
  line-height: 1;
}
.week-col-hdr.is-today .week-col-name { color: var(--gold); }

.week-col-num-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}
.week-col-num {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -.5px;
  line-height: 1;
}
.week-col-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--gold);
  color: #000;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* ── Body cells ── */
.week-col-body {
  padding: var(--sp2);             /* 1sp all sides — uniform */
  background: var(--bg);
  box-sizing: border-box;
  min-width: 0;
  min-height: 240px;        /* 30sp — comfortable minimum */
}
.week-col-body.is-today { background: rgba(184,115,51,.018); }

.week-col-empty {
  font-size: var(--fs-xs);
  color: var(--t4);
  text-align: center;
  padding-top: var(--sp6);        /* 3sp — vertically centers the dash */
  font-family: var(--mono);
  letter-spacing: .04em;
  user-select: none;
}

/* ── Event cards ── */
/*
  All cards: same padding, same radius, same border structure.
  Left border is 2px, so inner text has 6px true inset (8-2).
*/
.wev-card {
  border-radius: 4px;
  padding: var(--sp2) var(--sp2) var(--sp2) var(--sp2);  /* t r b l — l compensates for 2px border */
  margin-bottom: var(--sp1);
  cursor: pointer;
  transition: filter .12s;
  border-left: 2px solid transparent;
  overflow: hidden;
  box-sizing: border-box;
}
.wev-card:hover { filter: brightness(1.18); }
.wev-card.ev-work   { background: rgba(184,115,51,.09);  border-left-color: var(--gold); }
.wev-card.ev-health { background: rgba(74,158,116,.08);  border-left-color: var(--grn); }

/* Title — one line, ellipsis */
.wev-title {
  font-size: var(--fs-sm);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  margin-bottom: var(--sp1);
}
.wev-card.ev-work .wev-title   { color: var(--gold2); }
.wev-card.ev-health .wev-title { color: var(--grn); }

/* Time — mono, subtle */
.wev-time {
  font-size: var(--fs-xs);
  font-family: var(--mono);
  opacity: .58;
  line-height: 1;
  white-space: nowrap;
  letter-spacing: .02em;
}
.wev-card.ev-work .wev-time   { color: var(--gold); }
.wev-card.ev-health .wev-time { color: var(--grn); }

/* All-day chips — match event card structure */
.wev-allday {
  font-size: var(--fs-xs);
  padding: var(--sp1) var(--sp2) var(--sp1) var(--sp2); /* same inset logic as .wev-card */
  border-radius: 4px;
  margin-bottom: var(--sp1);
  background: rgba(184,115,51,.1);
  border-left: 2px solid var(--gold);
  color: var(--gold2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: background .1s;
  line-height: 1.4;
  font-weight: 500;
}
.wev-allday:hover { background: rgba(184,115,51,.18); }

/* Add-event slot */
.week-add-slot {
  font-size: var(--fs-xs);
  color: var(--t4);
  text-align: center;
  padding: var(--sp1) 0;
  border: 1px dashed var(--b1);
  border-radius: 4px;
  cursor: pointer;
  transition: border-color .12s, color .12s;
  font-family: var(--mono);
  margin-top: var(--sp2);
  user-select: none;
  letter-spacing: .04em;
}
.week-add-slot:hover { border-color: var(--gold); color: var(--gold); }

/* ════════════════════════════════════════════
   MONTH VIEW — uniform 112px rows
════════════════════════════════════════════ */
.month-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}

/* DOW header — exact same column template as body rows */
.month-hdr {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  height: 32px;             /* 4sp */
  background: var(--s1);
  border-bottom: 2px solid var(--b1);
  flex-shrink: 0;
}
.month-hdr-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: .14em;
  color: var(--t3);
  font-family: var(--mono);
  border-right: 1px solid var(--b1);
  line-height: 1;
}
.month-hdr-cell:last-child { border-right: none; }

/* Scrollable body */
.month-rows {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: var(--b2) transparent;
}

/*
  Each row is a 7-column grid.
  grid-template-rows is NOT used here — instead every
  .month-cell has a hard height so the row sizes to its cells.
  Using height (not min-height) prevents cells from growing.
*/
.month-row {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-bottom: 1px solid var(--b1);
}
.month-row:last-child { border-bottom: none; }

/* Cell — hard 112px, overflow clipped */
.month-cell {
  height: 112px;            /* 14sp — every cell identical */
  overflow: hidden;
  padding: var(--sp2);             /* 1sp all sides */
  background: var(--s1);
  border-right: 1px solid var(--b1);
  cursor: pointer;
  transition: background var(--t-fast);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;   /* stacks day-num + chips cleanly */
}
.month-cell:last-child { border-right: none; }
.month-cell:hover { background: var(--s2); }
.month-cell.empty {
  background: var(--bg);
  opacity: .05;
  cursor: default;
  pointer-events: none;
}
.month-cell.today { background: rgba(184,115,51,.05); }

/* Day number circle — 24×24, sits at top of cell */
.month-day-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11.5px;
  font-family: var(--mono);
  color: var(--t2);
  margin-bottom: var(--sp1);       /* 0.5sp gap before first chip */
  line-height: 1;
  flex-shrink: 0;
}
.month-cell.today .month-day-num {
  background: var(--gold);
  color: #000;
  font-weight: 700;
}

/* Event chips — consistent height, no wrap */
.month-ev-chip {
  display: block;
  font-size: 9.5px;
  font-weight: 500;
  padding: var(--sp1) var(--sp2) var(--sp1) var(--sp1);  /* l 4px to clear border */
  border-radius: 3px;
  margin-bottom: var(--sp1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  line-height: 1.4;
  border-left: 2px solid transparent;
  transition: opacity .1s;
  flex-shrink: 0;
}
.month-ev-chip:hover { opacity: .75; }
.month-ev-chip.ev-work   { background: rgba(184,115,51,.1);  color: var(--gold2); border-left-color: var(--gold); }
.month-ev-chip.ev-health { background: rgba(74,158,116,.09); color: var(--grn);   border-left-color: var(--grn); }

/* Overflow indicator */
.month-more {
  font-size: var(--fs-xs);
  color: var(--t3);
  font-family: var(--mono);
  padding-left: var(--sp1);
  line-height: 1.4;
  flex-shrink: 0;
}

/* ─── Event detail modal ─────────────────── */
.ev-modal-bg {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.72);
  backdrop-filter: blur(12px);
  z-index: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp6);
}
.ev-modal {
  background: var(--s1);
  border: 1px solid var(--b2);
  border-radius: var(--r3);
  width: 384px;             /* 48sp */
  max-width: 95vw;
  box-shadow: 0 24px 64px rgba(0,0,0,.9);
  animation: modalIn .18s cubic-bezier(.4,0,.2,1);
  overflow: hidden;
}
@keyframes modalIn {
  from { opacity: 0; transform: scale(.97) translateY(8px); }
  to   { opacity: 1; transform: scale(1)   translateY(0);   }
}
.ev-modal-hdr {
  padding: var(--sp4) var(--sp12) var(--sp4) var(--sp5); /* right 48px leaves room for close btn */
  border-bottom: 1px solid var(--b1);
  display: flex;
  flex-direction: column;
  gap: var(--sp1);
  position: relative;
}
.ev-modal-type {
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: .2em;
  font-family: var(--mono);
  line-height: 1;
}
.ev-modal-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -.25px;
  line-height: 1.25;
}
.ev-modal-meta {
  font-size: var(--fs-sm);
  color: var(--t2);
  font-family: var(--mono);
  line-height: 1;
}
.ev-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: 1px solid var(--b2);
  color: var(--t3);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all .12s;
  line-height: 1;
}
.ev-modal-close:hover { color: var(--ink); border-color: var(--b3); }
.ev-modal-body { padding: 0 var(--sp5) var(--sp2); }
.ev-row {
  display: flex;
  gap: var(--sp4);
  padding: var(--sp2) 0;
  border-bottom: 1px solid var(--b1);
  align-items: baseline;
}
.ev-row:last-child { border-bottom: none; }
.ev-row-lbl {
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: .15em;
  color: var(--t3);
  font-family: var(--mono);
  min-width: 56px;          /* 7sp */
  flex-shrink: 0;
  line-height: 1.6;
}
.ev-row-val {
  font-size: 12.5px;
  color: var(--ink);
  flex: 1;
  line-height: 1.5;
}

/* ════════════════════════════════════════════
   ONBOARDING — full-screen mobile-first flow
════════════════════════════════════════════ */
.ob-shell {
  position: fixed;
  inset: 0;
  background: var(--bg);
  z-index: 1200;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* Subtle radial glow at top for depth */
  background-image: radial-gradient(ellipse 60% 40% at 50% -10%, rgba(184,115,51,.06), transparent);
}

/* Scrollable content area */
.ob-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 0 0 var(--sp6);
}

/* Sticky footer with actions */
.ob-footer {
  position: sticky;
  bottom: 0;
  background: linear-gradient(to top, var(--bg) 85%, transparent);
  padding: var(--sp5) var(--sp6) calc(var(--sp5) + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: var(--sp2);
  flex-shrink: 0;
  z-index: 2;
}

/* Content padding */
.ob-content {
  padding: 0 var(--sp6);
  max-width: 560px;
  margin: 0 auto;
  width: 100%;
}

/* Header bar */
.ob-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--sp4) var(--sp6);
  padding-top: calc(var(--sp4) + env(safe-area-inset-top));
  flex-shrink: 0;
}

.ob-logo {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -.6px;
  color: var(--ink);
}
.ob-logo-dot { color: var(--gold); }

/* Progress bar */
.ob-progress-track {
  height: 2px;
  background: var(--b2);
  margin: 0 var(--sp6);
  border-radius: 2px;
  overflow: hidden;
  flex-shrink: 0;
  margin-bottom: var(--sp8);
}
.ob-progress-fill {
  height: 100%;
  background: var(--gold);
  border-radius: 2px;
  transition: width .4s cubic-bezier(.4,0,.2,1);
}

/* Step label */
.ob-step-label {
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: .2em;
  color: var(--gold);
  font-family: var(--mono);
  opacity: .8;
}

/* Titles */
.ob-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -.6px;
  line-height: 1.15;
  margin: var(--sp2) 0 var(--sp3);
}
.ob-sub {
  font-size: 15px;
  color: var(--t2);
  line-height: 1.7;
  margin-bottom: var(--sp8);
}

/* Input fields */
.ob-field {
  margin-bottom: var(--sp5);
}
.ob-field-label {
  font-size: var(--fs-sm);
  text-transform: uppercase;
  letter-spacing: .14em;
  color: var(--t3);
  font-family: var(--mono);
  margin-bottom: var(--sp2);
  display: block;
}
.ob-input {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 56px;
  background: var(--s1);
  border: 1.5px solid var(--b2);
  border-radius: var(--r3);
  color: var(--ink);
  font-family: var(--sans);
  font-size: 17px; /* Never below 16px — prevents iOS zoom */
  padding: 0 var(--sp4);
  outline: none;
  touch-action: manipulation;
  transition: border-color .15s, background .15s, box-shadow .15s;
  box-sizing: border-box;
}
.ob-input:focus {
  border-color: var(--gold);
  background: var(--s2);
  box-shadow: 0 0 0 3px rgba(184,115,51,.12);
}
.ob-input::placeholder { color: var(--t4); }

.ob-select {
  width: 100%;
  height: 56px;
  background: var(--s1);
  border: 1.5px solid var(--b2);
  border-radius: var(--r3);
  color: var(--ink);
  font-family: var(--sans);
  font-size: 17px;
  padding: 0 var(--sp4);
  outline: none;
  transition: border-color var(--t-fast);
  box-sizing: border-box;
  cursor: pointer;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237c7a74' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: var(--sp10);
}
.ob-select:focus { border-color: var(--gold); }
.ob-select option { background: #141414; }

/* Work type radio cards */
.ob-radio-group { display: flex; flex-direction: column; gap: var(--sp2); margin-bottom: var(--sp5); }
.ob-radio-card {
  display: flex;
  align-items: center;
  gap: var(--sp4);
  padding: var(--sp4) var(--sp4);
  background: var(--s1);
  border: 1.5px solid var(--b2);
  border-radius: var(--r3);
  cursor: pointer;
  transition: border-color .12s, background .12s;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.ob-radio-card.selected {
  border-color: var(--gold);
  background: rgba(184,115,51,.06);
}
.ob-radio-dot {
  width: 22px; height: 22px;
  border-radius: 50%;
  border: 2px solid var(--b3);
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: border-color .12s, background .12s;
}
.ob-radio-card.selected .ob-radio-dot {
  border-color: var(--gold);
  background: var(--gold);
}
.ob-radio-inner { width: 8px; height: 8px; border-radius: 50%; background: #000; }
.ob-radio-label { font-size: 15px; font-weight: 500; color: var(--ink); }
.ob-radio-desc  { font-size: var(--fs-sm); color: var(--t3); margin-top: var(--sp1); }

/* Buttons */
.ob-next {
  width: 100%;
  height: 56px;
  background: var(--gold);
  color: #000;
  border: none;
  border-radius: var(--r3);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background .12s, transform .1s;
  letter-spacing: -.1px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.ob-next:active { transform: scale(.98); }
.ob-next:disabled { opacity: .4; cursor: not-allowed; }
.ob-skip {
  width: 100%;
  height: 48px;
  background: transparent;
  border: 1.5px solid var(--b2);
  border-radius: var(--r3);
  color: var(--t2);
  font-size: 15px;
  cursor: pointer;
  font-family: var(--sans);
  transition: border-color .12s, color .12s;
  -webkit-tap-highlight-color: transparent;
}
.ob-skip:active { border-color: var(--b3); }

/* Feature cards on welcome */
.ob-features {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp2);
  margin-bottom: var(--sp8);
}
.ob-feat {
  display: flex;
  flex-direction: column;
  gap: var(--sp2);
  background: var(--s1);
  border: 1px solid var(--b1);
  border-radius: var(--r3);
  padding: var(--sp4);
  transition: border-color .12s;
}
.ob-feat-icon { font-size: 18px; color: var(--gold); opacity: .8; }
.ob-feat-label { font-size: var(--fs-base); font-weight: 600; color: var(--ink); }
.ob-feat-desc  { font-size: var(--fs-sm); color: var(--t3); line-height: 1.4; }

/* Sub items list */
.ob-sub-row { display: flex; flex-direction: column; gap: var(--sp2); }
.ob-sub-row-inner { display: grid; grid-template-columns: 1fr 100px 110px; gap: var(--sp2); }
.ob-subs-list { display: flex; flex-direction: column; gap: var(--sp2); margin-top: var(--sp3); }
.ob-sub-item {
  display: flex;
  align-items: center;
  gap: var(--sp3);
  background: var(--s1);
  border: 1px solid var(--b1);
  border-radius: var(--r2);
  padding: var(--sp3) var(--sp3);
  font-size: 14px;
}
.ob-sub-name { flex: 1; color: var(--ink); }
.ob-sub-amt  { color: var(--gold); font-family: var(--mono); font-size: var(--fs-sm); }
.ob-sub-del  { color: var(--t4); cursor: pointer; font-size: 18px; line-height: 1; padding: var(--sp1); transition: color .1s; }
.ob-sub-del:hover { color: var(--red); }

/* Goals */
.ob-goal-row { display: flex; flex-direction: column; gap: var(--sp2); }
.ob-goal-row-inner { display: grid; grid-template-columns: 1fr 120px; gap: var(--sp2); }
.ob-goals-list { display: flex; flex-direction: column; gap: var(--sp2); margin-top: var(--sp3); }
.ob-goal-item {
  display: flex;
  align-items: center;
  gap: var(--sp3);
  background: var(--s1);
  border: 1px solid var(--b1);
  border-radius: var(--r2);
  padding: var(--sp3) var(--sp3);
}
.ob-goal-title { flex: 1; font-size: 14px; color: var(--ink); }
.ob-goal-cat   { font-size: var(--fs-xs); color: var(--t3); font-family: var(--mono); }

/* Discogs box */
.ob-discogs-box {
  background: var(--s1);
  border: 1.5px solid var(--b2);
  border-radius: var(--r3);
  padding: var(--sp5);
  margin-bottom: var(--sp4);
}
.ob-discogs-title { font-size: 15px; font-weight: 600; color: var(--ink); margin-bottom: var(--sp2); }
.ob-discogs-sub   { font-size: var(--fs-base); color: var(--t2); margin-bottom: var(--sp4); line-height: 1.6; }

/* Completion */
.ob-complete { text-align: center; padding: var(--sp4) 0; }
.ob-complete-icon { display: block; margin: 0 auto var(--sp6); animation: obIconIn .4s cubic-bezier(.34,1.56,.64,1); }
@keyframes obIconIn { from { transform: scale(.5) rotate(-10deg); opacity:0; } to { transform: scale(1) rotate(0deg); opacity:1; } }
.ob-complete-title { font-size: 28px; font-weight: 700; color: var(--ink); letter-spacing: -.5px; margin-bottom: var(--sp3); }
.ob-complete-sub   { font-size: 15px; color: var(--t2); line-height: 1.7; margin-bottom: var(--sp7); }
.ob-complete-pills { display: flex; flex-wrap: wrap; gap: var(--sp2); justify-content: center; margin-bottom: var(--sp8); }
.ob-pill { background: var(--s1); border: 1px solid var(--b1); border-radius: var(--pill); padding: var(--sp2) var(--sp3); font-size: var(--fs-sm); color: var(--t2); font-family: var(--mono); }
.ob-pill.active { background: var(--goldbg); border-color: var(--goldbr); color: var(--gold2); }

/* Tip box */
.ob-tip {
  background: var(--s1);
  border: 1px solid var(--b1);
  border-left: 3px solid var(--gold);
  border-radius: var(--r2);
  padding: var(--sp3) var(--sp4);
  margin-bottom: var(--sp7);
  font-size: var(--fs-base);
  color: var(--t2);
  line-height: 1.6;
}
.ob-tip-label { font-size: var(--fs-xs); text-transform: uppercase; letter-spacing: .18em; color: var(--gold); fontFamily: var(--mono); margin-bottom: var(--sp1); opacity: .85; }

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--sp12) var(--sp6);
  text-align: center;
  color: var(--t3);
  gap: var(--sp2);
  min-height: 220px;
}
.empty-icon  { font-size: 30px; opacity: .2; margin-bottom: var(--sp2); line-height: 1; }
.empty-title { font-size: 14px; font-weight: 500; color: var(--t2); letter-spacing: -.1px; }
.empty-sub   { font-size: var(--fs-sm); color: var(--t3); max-width: 260px; line-height: 1.65; }
.empty-action { margin-top: var(--sp3); }

/* ════════════════════════════════════════════
   REMINDERS
════════════════════════════════════════════ */
.rm-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp2);
  margin-bottom: var(--sp4);
}
.rm-card {
  background: var(--s1);
  border: 1px solid var(--b1);
  border-radius: var(--r2);
  padding: var(--sp3) var(--sp4);
  display: flex;
  flex-direction: column;
  gap: var(--sp2);
  transition: border-color .12s, box-shadow .12s;
  position: relative;
}
.rm-card:hover { border-color: var(--b2); }
.rm-card.rm-enabled { border-left: 2px solid var(--gold); }
.rm-card.rm-disabled { opacity: .5; }
.rm-card-hdr { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--sp2); }
.rm-title { font-size: var(--fs-base); font-weight: 600; color: var(--ink); letter-spacing: -.15px; line-height: 1.3; flex: 1; }
.rm-msg { font-size: 11.5px; color: var(--t2); line-height: 1.55; }
.rm-meta { display: flex; align-items: center; gap: var(--sp2); margin-top: var(--sp1); }
.rm-time { font-size: var(--fs-xs); color: var(--gold); font-family: var(--mono); letter-spacing: .04em; }
.rm-toggle {
  width: 28px; height: 16px; border-radius: var(--r2);
  background: var(--b2); border: none; cursor: pointer;
  position: relative; flex-shrink: 0; transition: background var(--t-fast);
}
.rm-toggle.on { background: var(--gold); }
.rm-toggle::after {
  content: '';
  position: absolute;
  top: 2px; left: 2px;
  width: 12px; height: 12px;
  border-radius: 50%;
  background: #fff;
  transition: transform .15s;
}
.rm-toggle.on::after { transform: translateX(12px); }
.rm-del { background: transparent; border: none; color: var(--t4); cursor: pointer; font-size: 14px; line-height: 1; padding: 0 var(--sp1); transition: color .1s; flex-shrink: 0; }
.rm-del:hover { color: var(--red); }

/* AI generate panel */
.rm-gen-box {
  background: var(--s2);
  border: 1px solid var(--b1);
  border-top: 2px solid var(--gold);
  border-radius: var(--r2);
  padding: var(--sp4);
  margin-bottom: var(--sp4);
}
.rm-gen-row { display: flex; gap: var(--sp2); align-items: flex-end; flex-wrap: wrap; }
.rm-chip-row { display: flex; gap: var(--sp1); flex-wrap: wrap; margin-top: var(--sp2); }
.rm-chip {
  font-size: var(--fs-xs); color: var(--t2); background: transparent;
  border: 1px solid var(--b2); border-radius: var(--pill); padding: var(--sp1) var(--sp2);
  cursor: pointer; font-family: var(--mono); transition: color .1s, border-color .1s, background .1s;
}
.rm-chip:hover { color: var(--gold2); border-color: var(--gold); }
.rm-chip.selected { color: var(--gold2); border-color: var(--gold); background: var(--goldbg); }

/* Notification preview — lock screen mockup */
.rm-preview {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  border-radius: var(--r3);
  padding: var(--sp5);
  margin-bottom: var(--sp4);
  position: relative;
  overflow: hidden;
}
.rm-preview::before {
  content: '';
  position: absolute; inset: 0;
  background: rgba(0,0,0,.3);
  border-radius: var(--r3);
}
.rm-preview-time { position: relative; z-index: 1; font-size: 36px; font-weight: 200; color: #fff; text-align: center; letter-spacing: -1px; margin-bottom: var(--sp1); }
.rm-preview-date { position: relative; z-index: 1; font-size: var(--fs-sm); color: rgba(255,255,255,.6); text-align: center; margin-bottom: var(--sp4); font-family: var(--mono); letter-spacing: .04em; }
.rm-notif-bubble {
  position: relative; z-index: 1;
  background: rgba(255,255,255,.15);
  backdrop-filter: blur(12px);
  border-radius: var(--r3);
  padding: var(--sp3) var(--sp3);
  display: flex;
  gap: var(--sp2);
  align-items: flex-start;
  animation: fadeUp .25s ease;
}
.rm-notif-icon { font-size: 18px; flex-shrink: 0; margin-top: var(--sp1); }
.rm-notif-title { font-size: var(--fs-sm); font-weight: 600; color: #fff; margin-bottom: var(--sp1); }
.rm-notif-msg { font-size: var(--fs-sm); color: rgba(255,255,255,.8); line-height: 1.45; }
.rm-notif-time { font-size: var(--fs-xs); color: rgba(255,255,255,.45); margin-top: var(--sp1); font-family: var(--mono); }

/* JSON export panel */
.rm-json-box {
  background: var(--s2);
  border: 1px solid var(--b1);
  border-radius: var(--r2);
  padding: var(--sp3) var(--sp4);
  font-family: var(--mono);
  font-size: 10.5px;
  color: var(--t2);
  line-height: 1.7;
  overflow-x: auto;
  white-space: pre;
  max-height: 240px;
  overflow-y: auto;
}
.rm-json-key   { color: var(--gold); }
.rm-json-str   { color: var(--grn); }
.rm-json-num   { color: var(--gold2); }



/* ════════════════════════════════════════
   DESIGN SYSTEM — UNIFIED RULES
   One of everything. Nothing arbitrary.
════════════════════════════════════════ */

/* ── Page structure ── */
.page-body  { display: flex; flex-direction: column; gap: var(--sp4); }
.page-section { margin-bottom: var(--sp6); }
.page-section:last-child { margin-bottom: 0; }

/* ── Content rows — for key-value pairs, settings, etc ── */
.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp3);
  min-height: var(--row-h);
  padding: 0 var(--card-p);
  border-bottom: 1px solid var(--b1);
  box-sizing: border-box;
}
.row:last-child { border-bottom: none; }
.row-label { font-size: var(--fs-base); color: var(--ink); flex: 1; min-width: 0; }
.row-value { font-size: var(--fs-base); color: var(--t2); font-family: var(--mono); flex-shrink: 0; }

/* ── Empty state ── */
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--sp8) var(--sp6);
  text-align: center;
  gap: var(--sp3);
}
.empty-icon { font-size: 28px; opacity: .2; line-height: 1; }
.empty-msg  { font-size: var(--fs-sm); color: var(--t3); line-height: var(--lh-loose); max-width: 240px; }

/* ── Stat block — unified numbers display ── */
.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 1px;
  background: var(--b1);
  border-radius: var(--r3);
  overflow: hidden;
  margin-bottom: var(--sp4);
}
.stat {
  background: var(--s1);
  padding: var(--sp3) var(--sp4);
  display: flex;
  flex-direction: column;
  gap: var(--sp1);
}
.stat-n { font-size: var(--fs-title); font-weight: 700; color: var(--ink); letter-spacing: -.5px; line-height: 1; }
.stat-l { font-size: var(--fs-xs); color: var(--t3); font-family: var(--mono); text-transform: uppercase; letter-spacing: var(--ls-cap); }

/* ── Tag / badge ── */
.tag {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 var(--sp2);
  border-radius: var(--r);
  font-size: var(--fs-xs);
  font-family: var(--mono);
  font-weight: 500;
  letter-spacing: .04em;
  border: 1px solid var(--b2);
  color: var(--t2);
  background: var(--s2);
  white-space: nowrap;
}
.tag-gold  { background: var(--goldbg); border-color: var(--goldbr); color: var(--gold2); }
.tag-green { background: var(--grnbg);  border-color: var(--grnbr);  color: var(--grn);  }
.tag-red   { background: var(--redbg);  border-color: var(--redbr);  color: var(--red);  }

/* ── Action bar — buttons at bottom of a card ── */
.action-bar {
  display: flex;
  gap: var(--sp2);
  padding-top: var(--sp3);
  border-top: 1px solid var(--b1);
  margin-top: var(--sp4);
}
.action-bar .btn { flex: 1; justify-content: center; }

/* ── Offline indicator ── */
.offline-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--accent-s);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  opacity: 0.7;
}
.offline-pill {
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--s2);
  border: 1px solid var(--b2);
  border-top: none;
  border-radius: 0 0 var(--r2) var(--r2);
  padding: 3px var(--sp3);
  font-size: var(--fs-xs);
  font-family: var(--mono);
  color: var(--t3);
  white-space: nowrap;
  letter-spacing: .04em;
  white-space: nowrap;
  pointer-events: none;
}
.sync-dot {
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--grn);
  margin-right: var(--sp1);
  vertical-align: middle;
  animation: syncPulse 1.5s ease-in-out infinite;
}
@keyframes syncPulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: .3; }
}

.mylo-act {
  display: flex;
  align-items: center;
  gap: var(--sp2);
  width: 100%;
  padding: var(--sp3) var(--card-p);
  background: transparent;
  border: none;
  border-top: 1px solid var(--b1);
  color: var(--gold2);
  font-size: var(--fs-sm);
  font-family: var(--sans);
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: background var(--t-fast), color var(--t-fast);
  -webkit-tap-highlight-color: transparent;
  letter-spacing: -.1px;
}
.mylo-act:hover { background: var(--goldbg); }
.mylo-act:active { opacity: .7; }
.mylo-act-icon { font-size: 14px; opacity: .7; flex-shrink: 0; }
.mylo-act-label { flex: 1; }
.mylo-act-hint { font-size: var(--fs-xs); color: var(--t3); font-family: var(--mono); }

.act-strip {
  display: flex;
  gap: var(--sp2);
  padding: var(--sp2) var(--card-p) var(--sp3);
  border-top: 1px solid var(--b1);
  flex-wrap: wrap;
}
.dash-task .act-strip { padding: var(--sp2) 0 var(--sp2); }
.act-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--sp1);
  height: 28px;
  padding: 0 var(--sp3);
  background: var(--goldbg);
  border: 1px solid var(--goldbr);
  border-radius: var(--pill);
  color: var(--gold2);
  font-size: var(--fs-xs);
  font-family: var(--sans);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--t-fast), border-color var(--t-fast);
  -webkit-tap-highlight-color: transparent;
}
.act-chip:hover { background: rgba(184,115,51,.14); border-color: var(--gold); }
.act-chip:active { opacity: .7; }
.act-chip-icon { font-size: 11px; }
.action-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--sp1);
  margin-top: var(--sp2);
  padding: var(--sp1) var(--sp3);
  background: var(--grnbg);
  border: 1px solid var(--grnbr);
  border-radius: var(--pill);
  font-size: var(--fs-xs);
  color: var(--grn);
  font-family: var(--mono);
  font-weight: 500;
}

/* ══════════════════════════════════════
   DESKTOP (> 680px)
   Everything here overrides mobile-first
   values set in the ≤680px block.
══════════════════════════════════════ */
@media (min-width: 680px) {
  /* Sidebar always visible — reset mobile drawer */
  .sb          { position: relative; transform: none !important; box-shadow: none; width: 240px; max-width: 240px; flex-shrink: 0; }
  .mob-header  { display: none !important; }
  .bot-nav     { display: none !important; }
  .app         { flex-direction: row; }
  .main        { flex: 1; overflow-y: auto; padding-bottom: 0 !important; }
  .main.is-chat { overflow: hidden; }

  /* Page header — inline layout */
  .page-hdr         { flex-wrap: nowrap; align-items: center; }
  .page-hdr-left    { flex: 1; width: auto; }
    .page-hdr-actions > * { flex: none; }

  /* Desktop spacing scale */
  :root {
    --page-px:   var(--sp10);
    --page-py:   var(--sp6);
    --page-max:  1040px;
  --dashboard-max: 100%;
    --card-p:    var(--sp4);
    --card-gap:   var(--sp4);
    --input-h:   36px;
    --btn-h:     36px;
    --btn-sm-h:  32px;
    --row-h:     44px;
  }
}
/* ════════════════════════════════════════
   POWER DASHBOARD
════════════════════════════════════════ */

/* ── Dashboard header ── */
.dash-greeting {
  font-family: var(--sans);
  font-size: clamp(20px, 4vw, 26px);
  font-weight: 300;
  color: var(--ink);
  letter-spacing: -.5px;
  line-height: 1.1;
  margin-bottom: var(--sp2);
}
.dash-context {
  font-size: var(--fs-sm);
  color: var(--t3);
  font-family: var(--mono);
  letter-spacing: .1em;
  line-height: 1;
}

/* ── Pulse strip — key numbers at a glance ── */
.pulse {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: var(--b2);
  border-radius: var(--r3);
  overflow: hidden;
  margin-bottom: var(--sp4);
  box-shadow: 0 2px 12px rgba(0,0,0,.5), 0 0 0 1px rgba(184,115,51,.12);
}
.pulse-cell {
  background: var(--bg);
  padding: var(--sp3) var(--sp3);
  display: flex;
  flex-direction: column;
  gap: 3px;
  cursor: pointer;
  transition: background var(--t-fast);
}
.pulse-cell:active { background: var(--s2); }
.pulse-n {
  font-size: 22px;
  font-weight: 300;
  letter-spacing: -.5px;
  line-height: 1;
  color: var(--ink);
}
.pulse-l {
  font-size: 11px;
  text-transform: none;
  letter-spacing: 0;
  color: var(--t3);
  font-family: var(--sans);
  font-weight: 400;
}

/* ── Timeline — today's schedule ── */
.timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.tl-item {
  display: flex;
  gap: var(--sp3);
  padding: var(--sp2) 0;
  align-items: flex-start;
  border-bottom: 1px solid var(--b1);
  position: relative;
}
.tl-item:last-child { border-bottom: none; }
.tl-time {
  font-size: var(--fs-xs);
  color: var(--t3);
  font-family: var(--mono);
  min-width: 48px;
  padding-top: 2px;
  flex-shrink: 0;
  letter-spacing: .04em;
}
.tl-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--b3);
  margin-top: 5px;
  flex-shrink: 0;
  transition: background var(--t-fast);
}
.tl-dot.now { background: var(--gold); box-shadow: 0 0 6px rgba(184,115,51,.4); }
.tl-dot.health { background: var(--grn); }
.tl-dot.work { background: var(--blu); }
.tl-body { flex: 1; min-width: 0; }
.tl-title {
  font-size: var(--fs-base);
  color: var(--ink);
  line-height: 1.3;
  font-weight: 500;
}
.tl-sub {
  font-size: var(--fs-xs);
  color: var(--t3);
  margin-top: 2px;
  font-family: var(--mono);
}

/* ── Inline task row ── */
.dash-task {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--b1);
  transition: opacity var(--t-fast);
}
.dash-task:last-child { border-bottom: none; }
.dash-task.done { opacity: .4; }
.dash-task-check {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid var(--b3);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background var(--t-fast), border-color var(--t-fast);
  background: transparent;
}
.dash-task.done .dash-task-check {
  background: var(--grn);
  border-color: var(--grn);
}
.dash-task-text {
  font-size: var(--fs-base);
  color: var(--ink);
  line-height: 1.4;
  flex: 1;
  min-width: 0;
}
.dash-task.done .dash-task-text {
  text-decoration: line-through;
  color: var(--t3);
}
.dash-task-badge {
  font-size: 9px;
  font-family: var(--mono);
  text-transform: uppercase;
  letter-spacing: .1em;
  color: var(--t4);
  flex-shrink: 0;
}

/* ── Quick add task inline ── */
.dash-add {
  display: flex;
  gap: var(--sp2);
  margin-top: var(--sp3);
  padding-top: var(--sp3);
  border-top: 1px solid var(--b1);
}
.dash-add input {
  flex: 1;
  height: 32px;
  background: var(--s3);
  border: 1px solid var(--b2);
  border-radius: var(--r2);
  padding: 0 var(--sp3);
  color: var(--ink);
  font-family: var(--sans);
  font-size: var(--fs-sm);
  outline: none;
  transition: border-color var(--t-fast);
  min-width: 0;
}
.dash-add input:focus { border-color: var(--gold); }
.dash-add input::placeholder { color: var(--t4); }
.dash-add-btn {
  height: 32px;
  width: 32px;
  border-radius: var(--r2);
  background: var(--gold);
  border: none;
  color: var(--bg);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background var(--t-fast);
  -webkit-tap-highlight-color: transparent;
}
.dash-add-btn:active { background: var(--gold2); }

/* ── Focus card ── */
.focus-card {
  background: var(--s1);
  border: 1px solid rgba(184,115,51,.2);
  border-radius: var(--r3);
  padding: var(--sp4);
  margin-bottom: var(--sp4);
  cursor: pointer;
  transition: border-color var(--t-fast), background var(--t-fast);
  position: relative;
  overflow: hidden;
}
.focus-card::before {
  content: "";
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 3px;
  background: var(--gold);
  border-radius: var(--r) 0 0 var(--r);
}
.focus-card:active { background: rgba(184,115,51,.12); }
.focus-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: .2em;
  color: var(--gold);
  font-family: var(--mono);
  font-weight: 600;
  margin-bottom: var(--sp2);
}
.focus-text {
  font-size: var(--fs-md);
  font-weight: 500;
  color: var(--ink);
  line-height: var(--lh-loose);
  letter-spacing: -.1px;
}

/* ── Alert strip ── */
.alert-strip {
  display: flex;
  flex-direction: column;
  gap: var(--sp2);
  margin-bottom: var(--sp4);
}
.alert-item {
  display: flex;
  align-items: center;
  gap: var(--sp3);
  background: var(--goldbg);
  border: 1px solid var(--goldbr);
  border-radius: var(--r2);
  padding: var(--sp2) var(--sp3);
}
.alert-item.red {
  background: var(--redbg);
  border-color: var(--redbr);
}
.alert-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--gold);
  flex-shrink: 0;
}
.alert-item.red .alert-dot { background: var(--red); }
.alert-msg {
  font-size: var(--fs-sm);
  color: var(--ink);
  line-height: 1.4;
  flex: 1;
}

/* ── Section divider with label ── */
.dash-section {
  margin-bottom: var(--sp4);
}
.dash-section-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sp3);
}
.dash-section-title {
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: var(--ls-wide);
  font-family: var(--mono);
  font-weight: 500;
  color: var(--t3);
}
.dash-section-action {
  font-size: var(--fs-xs);
  color: var(--t3);
  font-family: var(--mono);
  cursor: pointer;
  text-decoration: none;
  transition: color var(--t-fast);
}
.dash-section-action:hover { color: var(--gold2); }

/* ── Finance bar ── */
.fin-bar {
  height: 4px;
  background: var(--b2);
  border-radius: 2px;
  overflow: hidden;
  margin-top: var(--sp2);
}
.fin-bar-fill {
  height: 100%;
  border-radius: 2px;
  background: var(--grn);
  transition: width .4s ease;
}

/* ── Training ring ── */
.training-row {
  display: flex;
  align-items: center;
  gap: var(--sp3);
  padding: var(--sp2) 0;
  border-bottom: 1px solid var(--b1);
}
.training-row:last-child { border-bottom: none; }
.training-label { font-size: var(--fs-base); color: var(--ink); flex: 1; }
.training-val { font-size: var(--fs-sm); color: var(--t2); font-family: var(--mono); }

@media (max-width: 680px) {
  .pulse { grid-template-columns: repeat(2, 1fr); }
}

/* ════════════════════════════════════════
   V61 REDESIGN — PREMIUM REFINEMENTS
   Goal: Apple-level clarity and calm
════════════════════════════════════════ */

/* ── Tighter page header ── */
.page-hdr-v2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sp6);
  padding-bottom: var(--sp4);
  border-bottom: 1px solid var(--b1);
}
.page-hdr-v2 .page-hdr-v2 .eyebrow { margin-bottom: var(--sp1); }

/* ── Task row — premium feel ── */
.task-item {
  display: flex;
  align-items: flex-start;
  gap: var(--sp3);
  padding: var(--sp3) 0;
  border-bottom: 1px solid var(--b0);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: opacity var(--t-fast);
}
.task-item:last-child { border-bottom: none; }
.task-item.done { opacity: .4; }
.task-check {
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  border: 1.5px solid var(--b3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  transition: background var(--t-fast), border-color var(--t-fast);
  flex-shrink: 0;
}
.task-check.checked {
  background: var(--grn);
  border-color: var(--grn);
}
.task-body { flex: 1; min-width: 0; }
.task-text {
  font-size: var(--fs-base);
  color: var(--ink);
  line-height: 1.4;
  font-weight: 400;
}
.task-item.done .task-text {
  text-decoration: line-through;
  color: var(--t3);
}
.task-meta {
  display: flex;
  align-items: center;
  gap: var(--sp2);
  margin-top: 3px;
}
.task-meta-item {
  font-size: 10px;
  color: var(--t4);
  font-family: var(--mono);
  letter-spacing: .06em;
}

/* ── Goal card — clean progress ── */
.goal-card {
  background: var(--s1);
  border: 1px solid var(--b2);
  border-radius: var(--r3);
  padding: var(--card-p);
  margin-bottom: var(--sp3);
  transition: border-color var(--t-fast);
}
.goal-card:hover { border-color: var(--b3); }
.goal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--sp3);
  gap: var(--sp3);
}
.goal-title {
  font-size: var(--fs-title);
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -.3px;
  line-height: 1.2;
  flex: 1;
}
.goal-pct {
  font-size: var(--fs-xl);
  font-weight: 700;
  letter-spacing: -1px;
  line-height: 1;
  flex-shrink: 0;
}
.goal-bar {
  height: 3px;
  background: var(--b2);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: var(--sp3);
}
.goal-bar-fill {
  height: 100%;
  border-radius: 2px;
  transition: width .6s cubic-bezier(.4, 0, .2, 1);
}
.goal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp2);
}
.goal-cat {
  font-size: var(--fs-xs);
  font-family: var(--mono);
  color: var(--t3);
  text-transform: uppercase;
  letter-spacing: var(--ls-cap);
}

/* ── Finance summary card ── */
.fin-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: var(--b1);
  border-radius: var(--r3);
  overflow: hidden;
  margin-bottom: var(--sp4);
}
.fin-summary-cell {
  background: var(--s1);
  padding: var(--sp4) var(--sp4);
  display: flex;
  flex-direction: column;
  gap: var(--sp1);
}
.fin-summary-n {
  font-size: var(--fs-xl);
  font-weight: 700;
  letter-spacing: -.8px;
  line-height: 1;
}
.fin-summary-l {
  font-size: var(--fs-xs);
  font-family: var(--mono);
  color: var(--t3);
  text-transform: uppercase;
  letter-spacing: var(--ls-cap);
}
.fin-net-bar {
  height: 4px;
  background: var(--b2);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: var(--sp5);
  margin-top: calc(-1 * var(--sp2));
}
.fin-net-fill {
  height: 100%;
  transition: width .5s ease;
}

/* ── Section divider with label ── */
.section-divider {
  display: flex;
  align-items: center;
  gap: var(--sp3);
  margin: var(--sp5) 0 var(--sp3);
}
.section-divider-line {
  flex: 1;
  height: 1px;
  background: var(--b1);
}
.section-divider-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: .2em;
  color: var(--t4);
  font-family: var(--mono);
  white-space: nowrap;
}

/* ── Inline add input ── */
.inline-add {
  display: flex;
  align-items: center;
  gap: var(--sp2);
  padding: var(--sp2) 0;
  border-bottom: 1px solid var(--b1);
}
.inline-add input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: var(--fs-base);
  color: var(--ink);
  font-family: var(--sans);
  padding: var(--sp2) 0;
  min-width: 0;
}
.inline-add input::placeholder { color: var(--t4); }
.inline-add-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--goldbg);
  border: 1px solid var(--goldbr);
  color: var(--gold2);
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background var(--t-fast), border-color var(--t-fast);
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}
.inline-add-btn:hover { background: var(--gold); border-color: var(--gold); color: var(--bg); }

/* ── Filter bar ── */
.filter-bar {
  display: flex;
  align-items: center;
  gap: var(--sp2);
  margin-bottom: var(--sp4);
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.filter-bar::-webkit-scrollbar { display: none; }
.filter-chip {
  height: 28px;
  padding: 0 var(--sp3);
  border-radius: var(--pill);
  border: 1px solid var(--b2);
  background: transparent;
  color: var(--t3);
  font-size: var(--fs-xs);
  font-family: var(--mono);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all var(--t-fast);
  -webkit-tap-highlight-color: transparent;
}
.filter-chip.active {
  background: var(--goldbg);
  border-color: var(--goldbr);
  color: var(--gold2);
  font-weight: 600;
}


/* ─── MORNING BRIEF ──────────────────────────────────────────────────────── */
.morning-brief {
  background: var(--s1);
  border-bottom: 1px solid var(--b2);
  position: sticky;
  top: 0;
  z-index: 100;
}
.mb-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--sp3) var(--sp4);
  cursor: pointer;
  gap: var(--sp3);
}
.mb-bar:active { background: var(--s2); }
.mb-left { display: flex; align-items: center; gap: var(--sp3); }
.mb-sun { font-size: 18px; line-height: 1; opacity: .85; }
.mb-headline { font-size: var(--fs-sm); font-weight: 600; color: var(--ink); letter-spacing: -.15px; }
.mb-sub { font-size: 11px; color: var(--t3); font-family: var(--mono); letter-spacing: .08em; text-transform: uppercase; margin-top: 2px; }
.mb-right { display: flex; align-items: center; gap: var(--sp2); flex-shrink: 0; }
.mb-badge {
  background: var(--red);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mb-chevron { font-size: 14px; color: var(--t3); transition: transform .2s; }
.mb-body { padding: var(--sp2) var(--sp4) var(--sp4); border-top: 1px solid var(--b1); }
.mb-section {
  margin-bottom: var(--sp3);
  cursor: pointer;
  padding: var(--sp2);
  border-radius: var(--r2);
  margin-left: calc(var(--sp3) * -1);
  margin-right: calc(var(--sp3) * -1);
}
.mb-section:hover { background: var(--s2); }
.mb-section-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--t4);
  letter-spacing: .12em;
  font-family: var(--mono);
  margin-bottom: var(--sp1);
}
.mb-row {
  display: flex;
  align-items: flex-start;
  gap: var(--sp2);
  font-size: var(--fs-sm);
  color: var(--t2);
  padding: 3px 0;
  line-height: 1.4;
}
.mb-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 6px;
}
.mb-dismiss {
  background: none;
  border: 1px solid var(--b2);
  border-radius: var(--r2);
  color: var(--t3);
  font-size: var(--fs-xs);
  padding: 5px 12px;
  cursor: pointer;
  margin-top: var(--sp2);
  font-family: var(--sans);
  transition: background var(--t-fast);
}
.mb-dismiss:hover { background: var(--s2); }

/* ─── COMMAND BAR ────────────────────────────────────────────────────────── */
/* Command bar trigger — mobile: floating pill above bot-nav.
   Desktop: inline at top of content column, static position, max 640px. */
.cmd-trigger {
  position: static;
  background: var(--s1);
  border: 1px solid var(--b2);
  border-radius: 999px;
  display: none; /* hidden on mobile — FAB in bottom nav handles it */
  align-items: center;
  gap: var(--sp3);
  padding: var(--sp2) var(--sp4);
  cursor: pointer;
  box-shadow: none;
  z-index: 200;
  transition: box-shadow .2s, background .15s, border-color .15s;
  min-width: 200px;
  max-width: 640px;
  width: 100%;
  height: 44px;
  box-sizing: border-box;
}
.cmd-trigger:hover { box-shadow: 0 6px 28px rgba(0,0,0,.35); background: var(--s2); }
.cmd-trigger:focus-visible { outline: none; border-color: var(--gold); box-shadow: 0 0 0 3px var(--accent-w); }
.cmd-trigger-icon { color: var(--accent-s); font-size: 14px; flex-shrink: 0; }
.cmd-trigger-label { flex: 1; text-align: left; font-size: var(--fs-sm); color: var(--t3); }
.cmd-trigger-kbd {
  font-size: 10px;
  color: var(--t3);
  background: var(--s2);
  border: 1px solid var(--b1);
  border-radius: var(--pill);
  padding: 2px 8px;
  font-family: var(--mono);
  flex-shrink: 0;
  letter-spacing: .02em;
}

@media (min-width: 680px) {
  .cmd-trigger {
    display: flex;
    position: static;
    transform: none;
    margin: 0 0 var(--sp5) 0;
    max-width: 640px;
    width: 100%;
    box-shadow: none;
    background: var(--s1);
    border-color: var(--b1);
    height: 48px;
  }
  .cmd-trigger:hover { background: var(--s2); box-shadow: 0 1px 4px rgba(0,0,0,.2); }
}
/* Hide kbd hint on mobile */
@media (max-width: 640px) { .cmd-trigger-kbd { display: none; } }

.cmd-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.6);
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;
  backdrop-filter: blur(4px);
}
.cmd-modal {
  background: var(--s1);
  border: 1px solid var(--b2);
  border-radius: var(--r4);
  width: 100%;
  max-width: 520px;
  margin: 0 var(--sp4);
  box-shadow: 0 24px 60px rgba(0,0,0,.5);
  overflow: hidden;
}
.cmd-input-wrap {
  display: flex;
  align-items: center;
  gap: var(--sp3);
  padding: var(--sp4);
  border-bottom: 1px solid var(--b1);
}
.cmd-icon { font-size: 18px; color: var(--gold); flex-shrink: 0; }
.cmd-input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font-size: var(--fs-md);
  color: var(--ink);
  font-family: var(--sans);
  caret-color: var(--gold);
}
.cmd-input::placeholder { color: var(--t4); }
.cmd-hint {
  display: flex;
  align-items: center;
  gap: var(--sp2);
  padding: var(--sp2) var(--sp4);
  background: rgba(184,115,51,.08);
  border-bottom: 1px solid var(--b1);
  cursor: pointer;
  transition: background var(--t-fast);
}
.cmd-hint:hover { background: rgba(184,115,51,.14); }
.cmd-hint-icon { color: var(--gold); font-size: 12px; }
.cmd-hint-label { flex: 1; font-size: var(--fs-sm); color: var(--gold); font-weight: 500; }
.cmd-hint-enter {
  font-size: 11px;
  color: var(--t4);
  background: var(--s2);
  border: 1px solid var(--b2);
  border-radius: 4px;
  padding: 1px 6px;
  font-family: var(--mono);
}
.cmd-examples {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp1);
  padding: var(--sp3) var(--sp4);
}
.cmd-example {
  background: var(--s2);
  border: 1px solid var(--b1);
  border-radius: 999px;
  font-size: 12px;
  color: var(--t3);
  padding: 4px 10px;
  cursor: pointer;
  font-family: var(--sans);
  transition: background .15s, color .15s;
}
.cmd-example:hover { background: var(--s3); color: var(--ink); }

/* ─── RELIABILITY: page-level error fallback ─────────────────────────────── */
.page-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: var(--sp3);
  padding: var(--sp6);
  text-align: center;
}
.page-error-icon { font-size: 32px; opacity: .4; }
.page-error-title { font-size: var(--fs-md); font-weight: 600; color: var(--ink); }
.page-error-msg { font-size: var(--fs-sm); color: var(--t3); max-width: 280px; line-height: 1.6; }
.page-error-btn {
  background: var(--s2);
  border: 1px solid var(--b2);
  border-radius: var(--r2);
  color: var(--t2);
  font-size: var(--fs-sm);
  padding: 8px 20px;
  cursor: pointer;
  font-family: var(--sans);
}

/* ─── WEEKLY REVIEW ─────────────────────────────────────────────────────── */
.wr-wrap { padding-bottom: 120px; }
.wr-header { display:flex; align-items:flex-end; justify-content:space-between; padding:var(--sp5) var(--card-p) var(--sp4); }
.wr-toggle { display:flex; background:var(--s2); border-radius:var(--r2); padding:3px; gap:3px; }
.wr-tog-btn { background:none; border:none; color:var(--t3); font-size:var(--fs-xs); font-family:var(--sans); font-weight:500; padding:5px 12px; border-radius:calc(var(--r2) - 2px); cursor:pointer; transition:background .15s, color .15s; }
.wr-tog-btn.on { background:var(--s1); color:var(--ink); box-shadow:0 1px 3px rgba(0,0,0,.2); }
.wr-warns { margin:0 var(--card-p) var(--sp3); display:flex; flex-direction:column; gap:var(--sp1); }
.wr-warn-row { display:flex; align-items:center; gap:var(--sp2); font-size:var(--fs-sm); color:var(--t2); background:rgba(220,80,80,.06); border-radius:var(--r2); padding:var(--sp2) var(--sp3); }
.wr-warn-dot { width:5px; height:5px; border-radius:50%; background:var(--red); flex-shrink:0; }
.wr-grid { display:grid; grid-template-columns:1fr 1fr; gap:var(--sp2); padding:0 var(--card-p) var(--sp4); }
.wr-stat { background:var(--s1); border:1px solid var(--b2); border-radius:var(--r3); padding:var(--sp3) var(--sp4); transition:background .15s; }
.wr-stat:active { background:var(--s2); }
.wr-stat-top { display:flex; align-items:baseline; gap:var(--sp2); margin-bottom:3px; }
.wr-stat-icon { font-size:12px; color:var(--t4); }
.wr-stat-val { font-size:22px; font-weight:700; letter-spacing:-.5px; line-height:1; }
.wr-stat-label { font-size:11px; color:var(--t3); font-weight:500; }
.wr-stat-sub { font-size:11px; color:var(--t4); margin-top:2px; }
.wr-section { background:var(--s1); border:1px solid var(--b2); border-radius:var(--r3); padding:var(--sp4); margin:0 var(--card-p) var(--sp3); }
.wr-section-label { font-size:10px; font-weight:700; color:var(--t4); letter-spacing:.12em; font-family:var(--mono); margin-bottom:var(--sp3); }
.wr-goal-row { display:flex; align-items:center; justify-content:space-between; }
.wr-goal-title { font-size:var(--fs-sm); font-weight:600; color:var(--ink); }
.wr-goal-pct { font-size:var(--fs-md); font-weight:700; }
.wr-goal-target { font-size:11px; color:var(--t4); margin-top:var(--sp2); }
.wr-goal-item { margin-bottom:var(--sp3); }
.wr-goal-item:last-child { margin-bottom:0; }
.wr-goal-name { font-size:var(--fs-sm); color:var(--t2); }
.wr-goal-pct-sm { font-size:var(--fs-xs); font-weight:600; }
.wr-row { display:flex; align-items:center; gap:var(--sp2); padding:var(--sp2) 0; border-bottom:1px solid var(--b1); }
.wr-row:last-child { border-bottom:none; padding-bottom:0; }
.wr-row-dot { width:5px; height:5px; border-radius:50%; flex-shrink:0; }
.wr-row-text { flex:1; font-size:var(--fs-sm); color:var(--t2); }
.wr-row-badge { font-size:11px; color:var(--t3); background:var(--s2); border-radius:var(--r1); padding:2px 7px; flex-shrink:0; font-family:var(--mono); }
.wr-empty { text-align:center; padding:var(--sp8) var(--sp6); }
.wr-empty-icon { font-size:32px; color:var(--t4); margin-bottom:var(--sp3); }
.wr-empty-title { font-size:var(--fs-md); font-weight:600; color:var(--t2); margin-bottom:var(--sp2); }
.wr-empty-sub { font-size:var(--fs-sm); color:var(--t4); line-height:1.6; max-width:260px; margin:0 auto; }

/* ─── CHAT HOME CONTEXT STRIP ────────────────────────────────────────────── */
.chat-context-strip { padding:var(--sp4) var(--sp4) 0; }
.ccs-greeting { font-size:var(--fs-lg); font-weight:700; color:var(--ink); letter-spacing:-.3px; margin-bottom:var(--sp3); }
.ccs-chips { display:flex; flex-wrap:wrap; gap:var(--sp2); }
.ccs-chip { display:flex; align-items:center; gap:6px; background:var(--s2); border:1px solid var(--b2); border-radius:var(--r2); padding:6px 12px; cursor:pointer; transition:background .15s; }
.ccs-chip:hover { background:var(--s3); }
.ccs-chip-grn { border-color:rgba(74,158,116,.3); background:rgba(74,158,116,.08); }
.ccs-chip-warn { border-color:rgba(220,80,80,.3); background:rgba(220,80,80,.06); }
.ccs-n { font-size:var(--fs-sm); font-weight:700; color:var(--ink); }
.ccs-l { font-size:var(--fs-xs); color:var(--t3); }


/* ─── WEEKLY REVIEW v2 ───────────────────────────────────────────── */
.wr2-wrap { padding: var(--sp4); padding-bottom: 120px; }
.wr2-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:var(--sp4); }
.wr2-headline { font-size:var(--fs-sm); color:var(--t3); margin-top:var(--sp1); line-height:1.5; max-width:260px; }
.wr2-toggle { display:flex; background:var(--s2); border-radius:var(--r2); padding:2px; gap:2px; flex-shrink:0; }
.wr2-tog { background:none; border:none; color:var(--t3); font-size:var(--fs-xs); font-family:var(--mono); padding:4px 10px; border-radius:calc(var(--r2) - 2px); cursor:pointer; letter-spacing:.08em; }
.wr2-tog.on { background:var(--s3); color:var(--ink); }
.wr2-alerts { background:rgba(220,53,69,.06); border:1px solid rgba(220,53,69,.15); border-radius:var(--r2); padding:var(--sp3); margin-bottom:var(--sp4); display:flex; flex-direction:column; gap:var(--sp2); }
.wr2-alert { display:flex; align-items:center; gap:var(--sp2); font-size:var(--fs-sm); color:var(--t2); }
.wr2-alert-dot { width:5px; height:5px; border-radius:50%; background:var(--red); flex-shrink:0; }
.wr2-grid { display:grid; grid-template-columns:1fr 1fr; gap:var(--sp2); margin-bottom:var(--sp4); }
.wr2-card { background:var(--s1); border:1px solid var(--b1); border-radius:var(--r3); padding:var(--sp3); }
.wr2-card:active { background:var(--s2); }
.wr2-card-top { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:var(--sp2); }
.wr2-card-icon { font-size:16px; opacity:.5; }
.wr2-card-val { font-size:22px; font-weight:700; color:var(--ink); letter-spacing:-.5px; line-height:1; margin-bottom:3px; }
.wr2-card-label { font-size:11px; color:var(--t3); font-family:var(--mono); letter-spacing:.06em; text-transform:uppercase; }
.wr2-card-sub { font-size:11px; color:var(--t4); margin-top:3px; }
.wr2-section { background:var(--s1); border:1px solid var(--b1); border-radius:var(--r3); padding:var(--sp3); margin-bottom:var(--sp2); }
.wr2-section:active { background:var(--s2); }
.wr2-section-lbl { font-size:10px; font-weight:700; color:var(--t4); letter-spacing:.14em; font-family:var(--mono); text-transform:uppercase; margin-bottom:var(--sp2); }
.wr2-goal-hero { display:flex; align-items:center; gap:var(--sp3); }
.wr2-goal-title { font-size:var(--fs-sm); font-weight:600; color:var(--ink); margin-bottom:4px; }
.wr2-goal-target { font-size:var(--fs-xs); color:var(--t3); margin-bottom:var(--sp2); }
.wr2-bar-wrap { height:4px; background:var(--b2); border-radius:99px; overflow:hidden; }
.wr2-bar-fill { height:100%; border-radius:99px; transition:width .5s cubic-bezier(.4,0,.2,1); }
.wr2-goal-row { margin-bottom:var(--sp2); }
.wr2-goal-row:last-child { margin-bottom:0; }
.wr2-goal-row-top { display:flex; justify-content:space-between; margin-bottom:4px; }
.wr2-goal-row-name { font-size:var(--fs-sm); color:var(--t2); }
.wr2-goal-row-pct { font-size:var(--fs-xs); font-family:var(--mono); }
.wr2-row { display:flex; align-items:center; gap:var(--sp2); padding:4px 0; }
.wr2-dot { width:5px; height:5px; border-radius:50%; flex-shrink:0; }
.wr2-row-text { flex:1; font-size:var(--fs-sm); color:var(--t2); }
.wr2-badge { font-size:11px; color:var(--t3); background:var(--s2); border:1px solid var(--b1); border-radius:var(--r1); padding:1px 7px; font-family:var(--mono); flex-shrink:0; }
.wr2-empty { text-align:center; padding:var(--sp8) var(--sp4); }
.wr2-empty-icon { font-size:32px; opacity:.2; margin-bottom:var(--sp3); }
.wr2-empty-title { font-size:var(--fs-md); font-weight:600; color:var(--ink); margin-bottom:var(--sp2); }
.wr2-empty-sub { font-size:var(--fs-sm); color:var(--t3); max-width:260px; margin:0 auto; line-height:1.65; }


/* ─── SHIFT MODE ──────────────────────────────────────────────────── */
.shift-wrap { min-height:100dvh; padding:var(--sp5) var(--sp4) 120px; display:flex; flex-direction:column; gap:var(--sp3); max-width:480px; margin:0 auto; }
.shift-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:var(--sp2); }
.shift-greeting { font-size:26px; font-weight:700; color:var(--ink); letter-spacing:-.5px; line-height:1.2; }
.shift-date { font-size:var(--fs-sm); color:var(--t3); margin-top:4px; font-family:var(--mono); letter-spacing:.04em; }
.shift-exit-btn { background:none; border:1px solid var(--b2); border-radius:var(--r2); color:var(--t3); font-size:14px; width:32px; height:32px; display:flex; align-items:center; justify-content:center; cursor:pointer; flex-shrink:0; }
.shift-card { background:var(--s1); border:1px solid var(--b2); border-radius:var(--r3); padding:var(--sp4); }
.shift-card-primary { border-color:var(--gold); background:rgba(184,115,51,.06); }
.shift-card-label { font-size:10px; font-weight:700; color:var(--t4); letter-spacing:.14em; font-family:var(--mono); text-transform:uppercase; margin-bottom:var(--sp2); display:flex; align-items:center; gap:var(--sp2); }
.shift-card-count { background:var(--gold); color:var(--bg); font-size:10px; font-weight:700; width:18px; height:18px; border-radius:50%; display:flex; align-items:center; justify-content:center; }
.shift-card-title { font-size:var(--fs-lg); font-weight:600; color:var(--ink); letter-spacing:-.2px; }
.shift-card-sub { font-size:var(--fs-sm); color:var(--t3); margin-top:4px; }
.shift-card-empty { font-size:var(--fs-sm); color:var(--t4); cursor:pointer; }
.shift-card-empty:active { color:var(--gold); }
.shift-tasks { display:flex; flex-direction:column; gap:var(--sp2); }
.shift-task { display:flex; align-items:center; gap:var(--sp3); padding:var(--sp2) 0; cursor:pointer; }
.shift-task:active { opacity:.6; }
.shift-task-check { width:20px; height:20px; border-radius:50%; border:2px solid var(--b2); flex-shrink:0; }
.shift-task-text { font-size:var(--fs-md); color:var(--t2); flex:1; }
.shift-task-more { font-size:var(--fs-sm); color:var(--gold); cursor:pointer; padding-top:var(--sp1); }
.shift-stats { display:grid; grid-template-columns:1fr 1fr 1fr; gap:var(--sp2); }
.shift-stat { background:var(--s1); border:1px solid var(--b1); border-radius:var(--r3); padding:var(--sp3); text-align:center; cursor:pointer; }
.shift-stat:active { background:var(--s2); }
.shift-stat-val { font-size:20px; font-weight:700; color:var(--ink); letter-spacing:-.3px; line-height:1; margin-bottom:4px; }
.shift-stat-label { font-size:10px; color:var(--t4); font-family:var(--mono); letter-spacing:.08em; text-transform:uppercase; }
.shift-voice-btn { width:100%; height:80px; background:var(--gold); border:none; border-radius:var(--r3); display:flex; align-items:center; justify-content:center; gap:var(--sp3); cursor:pointer; transition:opacity .15s; margin-top:var(--sp2); }
.shift-voice-btn:active { opacity:.85; }
.shift-voice-icon { font-size:28px; color:var(--bg); }
.shift-voice-label { font-size:var(--fs-md); font-weight:700; color:var(--bg); letter-spacing:-.2px; }
.shift-footer { font-size:11px; color:var(--t4); text-align:center; font-family:var(--mono); letter-spacing:.06em; margin-top:auto; padding-top:var(--sp4); }

/* ─── ONBOARDING DEMO STEP ────────────────────────────────────────── */
.ob-demo-wrap { background:var(--s1); border:1px solid var(--b2); border-radius:var(--r3); overflow:hidden; margin-bottom:var(--sp4); }
.ob-demo-input-row { display:flex; align-items:center; gap:var(--sp3); padding:var(--sp3) var(--sp4); border-bottom:1px solid var(--b1); }
.ob-demo-result { padding:var(--sp3) var(--sp4); display:flex; align-items:center; gap:var(--sp2); }
.ob-demo-chips { padding:var(--sp2) var(--sp4); display:flex; flex-wrap:wrap; gap:var(--sp1); }
.ob-demo-chip { background:var(--s2); border:1px solid var(--b1); border-radius:999px; font-size:12px; color:var(--t3); padding:4px 10px; cursor:pointer; font-family:var(--sans); }

/* ─── CHAT CONTEXT CHIPS ──────────────────────────────────────────── */
.ccs-chip { display:inline-flex; align-items:center; gap:5px; background:var(--s2); border:1px solid var(--b2); border-radius:999px; padding:4px 10px; cursor:pointer; transition:background .12s; flex-shrink:0; }
.ccs-chip:active { background:var(--s3); }
.ccs-chip-grn { border-color:rgba(74,158,116,.3); background:rgba(74,158,116,.08); }
.ccs-chip-warn { border-color:rgba(220,53,69,.2); background:rgba(220,53,69,.06); }
.ccs-n { font-size:12px; font-weight:700; color:var(--ink); font-family:var(--mono); }
.ccs-l { font-size:11px; color:var(--t3); white-space:nowrap; }
.ccs-greeting { font-size:var(--fs-sm); font-weight:600; color:var(--ink); margin-bottom:var(--sp2); }
.chat-context-strip { padding:var(--sp3) var(--sp4); border-bottom:1px solid var(--b1); background:var(--s1); }
.ccs-chips { display:flex; gap:var(--sp2); overflow-x:auto; padding-bottom:2px; scrollbar-width:none; }
.ccs-chips::-webkit-scrollbar { display:none; }


/* ─── PATTERN INSIGHTS ────────────────────────────────────────────── */
.pat-insight { display:flex; align-items:flex-start; justify-content:space-between; gap:var(--sp3); padding:var(--sp3) var(--sp4); background:rgba(220,53,69,.04); border:1px solid rgba(220,53,69,.12); border-radius:var(--r2); margin-bottom:var(--sp2); }
.pat-insight-info { background:rgba(184,115,51,.04); border-color:rgba(184,115,51,.12); }
.pat-insight-left { display:flex; align-items:flex-start; gap:var(--sp2); flex:1; min-width:0; }
.pat-insight-dot { width:6px; height:6px; border-radius:50%; flex-shrink:0; margin-top:5px; }
.pat-insight-msg { font-size:var(--fs-sm); color:var(--t2); line-height:1.45; }
.pat-insight-action { font-size:11px; color:var(--t4); margin-top:3px; font-family:var(--mono); letter-spacing:.04em; }
.pat-insight-cat { font-size:10px; color:var(--t4); font-family:var(--mono); letter-spacing:.1em; text-transform:uppercase; flex-shrink:0; margin-top:2px; }


/* ─── SKELETON LOADERS ───────────────────────────────────────────── */
@keyframes shimmer {
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
}
.skeleton {
  background: linear-gradient(90deg, var(--s2) 25%, var(--s3) 50%, var(--s2) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
  border-radius: var(--r2);
}
.skeleton-text  { height: 13px; margin-bottom: 8px; }
.skeleton-title { height: 20px; width: 60%; margin-bottom: 12px; }
.skeleton-card  { height: 80px; border-radius: var(--r3); margin-bottom: var(--card-gap); }

/* ─── TOUCH RIPPLE ───────────────────────────────────────────────── */
@keyframes ripple {
  from { transform: scale(0); opacity: .3; }
  to   { transform: scale(2.5); opacity: 0; }
}

/* ─── BOTTOM NAV ACTIVE INDICATOR ────────────────────────────────── */
/* ─── LOADING SPINNER ────────────────────────────────────────────── */
@keyframes spin { to { transform: rotate(360deg); } }
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--b2);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: spin .7s linear infinite;
  display: inline-block;
}
.spinner-sm { width: 14px; height: 14px; border-width: 1.5px; }

/* ─── TYPING INDICATOR ────────────────────────────────────────────── */
/* Improved — three dots with stagger */
.typing-dots { display: inline-flex; gap: 3px; align-items: center; padding: 4px 0; }
.typing-dots span {
  width: 5px; height: 5px;
  background: var(--t3);
  border-radius: 50%;
  animation: tdot .9s ease-in-out infinite;
}
.typing-dots span:nth-child(2) { animation-delay: .15s; }
.typing-dots span:nth-child(3) { animation-delay: .30s; }

/* ─── STREAMING CURSOR ──────────────────────────────────────────── */
@keyframes blink { 0%,100% { opacity:1; } 50% { opacity:0; } }
.streaming-cursor {
  display: inline-block;
  width: 2px;
  height: 14px;
  background: var(--gold);
  margin-left: 2px;
  vertical-align: middle;
  border-radius: 1px;
  animation: blink .7s ease infinite;
}

/* ─── BOTTOM NAV PRIMARY ITEM ────────────────────────────────────── */






/* ─── DESKTOP SINGLE-COLUMN CONTENT ──────────────────────────────── */
@media (min-width: 680px) {
  .main-inner {
    display: block;
    max-width: var(--page-max);
    margin: 0 auto;
    padding: 0 var(--sp5);
    width: 100%;
    box-sizing: border-box;
  }

  .main-inner .page { margin: 0; }
}

/* Legacy .desk-aside rules kept below but unused (no markup renders them). */
@media (min-width: 768px) {
  .desk-aside {
    display: flex;
    flex-direction: column;
    gap: var(--sp3);
    padding-top: var(--sp4);
    position: sticky;
    top: var(--sp4);
    max-height: calc(100vh - var(--sp8));
    overflow-y: auto;
  }
}
@media (max-width: 768px) {
  .desk-aside { display: none; }
}
.desk-aside-card {
  background: var(--s1);
  border: 1px solid rgba(255,255,255,.05);
  border-radius: var(--r3);
  padding: var(--sp4);
  box-shadow: 0 2px 8px rgba(0,0,0,.3);
}
.desk-aside-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--t4);
  letter-spacing: .16em;
  text-transform: uppercase;
  font-family: var(--mono);
  margin-bottom: var(--sp3);
}

/* ─── DISPLAY TYPOGRAPHY ────────────────────────────────────────── */
.display-text {
  font-family: var(--display);
  font-weight: 400;
  letter-spacing: -.5px;
  line-height: 1.1;
}
.display-lg  { font-size: clamp(28px, 5vw, 40px); }
.display-md  { font-size: clamp(22px, 4vw, 30px); }
.greeting-word {
  font-family: var(--display);
  font-style: italic;
  color: var(--gold);
}

/* ── ASK MYLO FAB — center of bottom nav ── */
.bot-ni.is-mylo {
  flex: 1;
}
.bot-ni.is-mylo .bot-ni-ic {
  width: 24px;
  height: 24px;
}
/* Auburn outline ring around Mylo icon */
.mylo-ring {
  width: 30px;
  height: 30px;
  border: 1.2px solid var(--b3);
  border-radius: 8px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--t2);
  transition: background var(--t-fast);
  flex-shrink: 0;
}
.bot-ni.is-mylo:active .mylo-ring {
  background: var(--accent-w);
}
.bot-ni.is-mylo.on .mylo-ring {
  background: var(--accent-w);
}
.bot-ni.is-mylo .bot-ni-lbl {
  color: var(--accent, #B5694A);
  font-weight: 500;
}
/* Non-FAB items slightly smaller when FAB is center */
.bot-ni:not(.is-fab) {
  flex: 1;
}

/* ── CHAT AS HOME — full immersion when on ask tab ── */
.main.is-chat {
  background: var(--bg);
}

/* ── CHAT TOP — minimal chrome, logo only ── */
.chat-top {
  padding: var(--sp3) var(--sp4);
  border-bottom: none;
  background: transparent;
  flex-shrink: 0;
}

/* ── CHAT SHELL — breathes more ── */
@media (min-width: 680px) {
  .chat-shell {
    max-width: 720px;
    margin: 0 auto;
    width: 100%;
  }
}

/* ── MSG BUBBLE — rounder, more distinct ── */
.msg-user .msg-body {
  background: var(--s2);
  border-radius: var(--r3);
  padding: var(--sp3) var(--sp4);
  border: 1px solid var(--b2);
}
.msg .msg-meta {
  font-size: 10px;
  letter-spacing: .1em;
  color: var(--t4);
  margin-bottom: var(--sp1);
  font-family: var(--mono);
}

/* ── DASHBOARD — secondary feel when not home ── */
.page .dash-greeting {
  font-size: clamp(28px, 7vw, 42px);
}

/* ── PROACTIVE INSIGHT in chat ── */
.chat-proactive-warn {
  display: flex;
  align-items: center;
  gap: var(--sp2);
  padding: var(--sp2) var(--sp3);
  background: rgba(196,96,96,.08);
  border: 1px solid rgba(196,96,96,.2);
  border-radius: var(--r2);
  margin: var(--sp2) 0;
  font-size: var(--fs-sm);
  color: var(--t2);
}
.chat-proactive-warn-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--red);
  flex-shrink: 0;
}

/* ── REMOVE TRACE side effects — also strip the debug console style ── */

/* ── CHAT MESSAGES — user right, AI left ── */
.msg { display: flex; gap: var(--sp3); padding: var(--sp2) 0; align-items: flex-start; }
.msg-user { flex-direction: row-reverse; }
.msg-user .msg-body-user {
  background: var(--gold);
  color: #000;
  border-radius: var(--r3) var(--r3) var(--r2) var(--r3);
  padding: var(--sp3) var(--sp4);
  font-size: var(--fs-sm);
  line-height: 1.6;
  max-width: 80%;
  display: inline-block;
}
.msg-user .msg-body-user p { margin: 0; }
.msg-body {
  font-size: var(--fs-md);
  line-height: 1.65;
  color: var(--ink);
  flex: 1;
}
.msg-body p { margin: 0 0 var(--sp2); }
.msg-body p:last-child { margin-bottom: 0; }
.msg-av {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  font-family: var(--mono);
  flex-shrink: 0;
  margin-top: 3px;
}
.av-mylo {
  background: var(--gold);
  color: var(--bg);
}
.av-user {
  background: var(--s3);
  color: var(--t2);
  border: 1px solid var(--b2);
}

/* ── PAGE TITLE CANONICAL ── */
.page-title { font-size: var(--fs-xl); font-weight: 700; letter-spacing: -.8px; line-height: 1; color: var(--ink); margin-bottom: 0; }

.focus-arrow {
  display: inline-block;
  margin-left: var(--sp2);
  color: var(--gold2);
  font-size: var(--fs-sm);
  opacity: .7;
  transition: transform var(--t-fast), opacity var(--t-fast);
}
.focus-card:hover .focus-arrow { transform: translateX(4px); opacity: 1; }
.focus-card:active { transform: scale(.99); background: var(--s3); }

.field input:focus-visible,
.field select:focus-visible,
.field textarea:focus-visible {
  outline: none;
  border-color: var(--gold);
  box-shadow: 0 0 0 3px rgba(184,115,51,.15);
}

.seg {
  display: flex;
  background: var(--s2);
  border-radius: var(--r2);
  padding: 3px;
  gap: 3px;
  margin-bottom: var(--sp4);
}
.seg-btn {
  flex: 1;
  height: 34px;
  background: transparent;
  border: none;
  color: var(--t3);
  font-size: var(--fs-sm);
  font-weight: 500;
  border-radius: calc(var(--r2) - 2px);
  cursor: pointer;
  transition: background var(--t-fast), color var(--t-fast);
  font-family: var(--sans);
  white-space: nowrap;
}
.seg-btn.is-active {
  background: var(--s1);
  color: var(--ink);
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0,0,0,.35), 0 0 0 1px rgba(255,255,255,.06);
}
.seg-btn:active { opacity: .8; }

/* Narrow-desktop main-inner: kept single-column per sidebar pass; .desk-aside no longer rendered. */

.btn:disabled,
button:disabled {
  opacity: .35;
  cursor: not-allowed;
  pointer-events: none;
}
.btn-gold:disabled {
  opacity: .4;
  cursor: not-allowed;
  pointer-events: none;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 400;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: fadeIn var(--t-base) ease;
}
.modal-sheet {
  background: var(--s1);
  border-radius: var(--r4) var(--r4) 0 0;
  width: 100%;
  max-width: 560px;
  padding: var(--sp4);
  padding-bottom: max(var(--sp6), env(safe-area-inset-bottom));
  border-top: 1px solid var(--b2);
  animation: slideUp var(--t-spring) ease;
  max-height: 85vh;
  overflow-y: auto;
}
@media (min-width: 680px) {
  .modal-overlay { align-items: center; }
  .modal-sheet   { border-radius: var(--r4); max-width: 480px; }
}
@keyframes slideUp {
  from { transform: translateY(40px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

.field.error input,
.field.error select,
.field.error textarea {
  border-color: var(--red);
  box-shadow: 0 0 0 3px rgba(196,96,96,.12);
}
.field-error-msg {
  font-size: var(--fs-xs);
  color: var(--red);
  margin-top: var(--sp1);
  font-family: var(--mono);
}
.field-success input {
  border-color: var(--grn);
  box-shadow: 0 0 0 3px rgba(74,158,116,.1);
}

.settings-section {
  margin-bottom: var(--sp6);
}
.settings-section-label {
  font-size: var(--fs-xs);
  font-weight: 700;
  color: var(--t4);
  letter-spacing: var(--ls-label);
  text-transform: uppercase;
  font-family: var(--mono);
  padding: var(--sp3) 0 var(--sp2);
  border-bottom: 1px solid var(--b1);
  margin-bottom: var(--sp3);
}
.settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--sp3) 0;
  border-bottom: 1px solid var(--b1);
  min-height: var(--tap);
}
.settings-row:last-child { border-bottom: none; }
.settings-row-label { font-size: var(--fs-sm); color: var(--ink); font-weight: 500; }
.settings-row-sub   { font-size: var(--fs-xs); color: var(--t3); margin-top: 2px; }

.chat-bar-wrap {
  padding-bottom: env(safe-area-inset-bottom);
  background: var(--s1);
  border-top: 1px solid var(--b1);
}

input[type=number]::-webkit-outer-spin-button,
input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
input[type=number] { -moz-appearance: textfield; }
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px var(--s2) inset;
  -webkit-text-fill-color: var(--ink);
  caret-color: var(--ink);
}

@media print {
  .bot-nav, .mob-header, .sb, .cmd-trigger { display: none !important; }
  .page { max-width: 100%; padding: 0; }
  .card { box-shadow: none; border: 1px solid #ccc; }
}


/* ── SWIPE TO COMPLETE ── */
.task-swipe-wrap {
  position: relative;
  overflow: hidden;
  border-radius: var(--r2);
}
.task-swipe-bg {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  padding: 0 var(--sp4);
  font-size: var(--fs-sm);
  font-weight: 600;
  pointer-events: none;
  transition: opacity var(--t-fast);
}
.task-swipe-bg.complete {
  background: var(--grn);
  color: #fff;
  justify-content: flex-start;
}
.task-swipe-bg.later {
  background: var(--s3);
  color: var(--t2);
  justify-content: flex-end;
}
.task-swipe-inner {
  position: relative;
  background: var(--s1);
  transition: transform var(--t-fast);
  touch-action: pan-y;
  cursor: pointer;
  user-select: none;
}
.task-swipe-inner.swiping {
  transition: none;
}



/* ── SMART REPLIES ── */
.smart-replies {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp2);
  padding: var(--sp2) var(--sp4) var(--sp3);
  animation: fadeIn var(--t-base) ease;
}
.smart-reply-chip {
  height: 32px;
  padding: 0 var(--sp3);
  border-radius: 99px;
  background: var(--s2);
  border: 1px solid var(--b2);
  color: var(--t2);
  font-size: var(--fs-xs);
  font-family: var(--sans);
  font-weight: 500;
  cursor: pointer;
  transition: background var(--t-fast), color var(--t-fast), border-color var(--t-fast);
  white-space: nowrap;
}
.smart-reply-chip:hover {
  background: var(--s3);
  border-color: var(--gold);
  color: var(--gold2);
}
.smart-reply-chip:active { transform: scale(.96); }



/* ── FINANCE RUNWAY HERO ── */
.fin-runway-hero {
  background: var(--s1);
  border: 1px solid var(--b2);
  border-radius: var(--r3);
  padding: var(--sp6) var(--sp4);
  text-align: center;
  margin-bottom: var(--sp4);
}
.fin-runway-hero-n {
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -2px;
  line-height: 1;
  margin-bottom: var(--sp2);
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: var(--sp2);
}
.fin-runway-hero-unit {
  font-size: var(--fs-lg);
  font-weight: 500;
  letter-spacing: -.5px;
  color: var(--t2);
}
.fin-runway-hero-sub {
  font-size: var(--fs-sm);
  color: var(--t3);
  font-family: var(--mono);
}
@media (min-width: 560px) {
  .fin-runway-hero-n { font-size: 64px; }
}



/* ── TONE SELECTOR ── */
.tone-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp2);
}
.tone-card {
  padding: var(--sp3);
  border-radius: var(--r2);
  border: 1px solid var(--b2);
  background: var(--s2);
  cursor: pointer;
  transition: border-color var(--t-fast), background var(--t-fast);
}
.tone-card:hover { border-color: var(--b3); background: var(--s3); }
.tone-card.on {
  border-color: var(--gold);
  background: rgba(184,115,51,.08);
}
.tone-icon  { font-size: 18px; margin-bottom: var(--sp1); }
.tone-label { font-size: var(--fs-sm); font-weight: 600; color: var(--ink); margin-bottom: 2px; }
.tone-sub   { font-size: var(--fs-xs); color: var(--t3); line-height: 1.4; }



/* ── UNIFIED TASKS ── */
.unified-tasks { display: flex; flex-direction: column; gap: var(--sp2); }
.utask-section { }
.utask-section-label {
  font-size: var(--fs-xs);
  font-weight: 700;
  color: var(--t4);
  letter-spacing: var(--ls-label);
  text-transform: uppercase;
  font-family: var(--mono);
  padding: var(--sp3) 0 var(--sp2);
  border-bottom: 1px solid var(--b1);
  margin-bottom: var(--sp2);
}
.task-stats-row {
  display: flex;
  align-items: center;
  gap: var(--sp2);
  flex-wrap: wrap;
}
.task-stat-pill {
  font-size: var(--fs-xs);
  font-family: var(--mono);
  color: var(--t3);
  background: var(--s2);
  border: 1px solid var(--b1);
  border-radius: 99px;
  padding: 2px var(--sp2);
}
.task-stat-pill.done { color: var(--grn); }



@keyframes pageSlideIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}




.chat-empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--sp8);
  text-align: center;
  animation: fadeIn var(--t-base) ease;
}



/* Subtle scale feedback on interactive elements */
.haptic:active { transform: scale(.97); transition: transform 60ms; }
.haptic-sm:active { transform: scale(.95); transition: transform 60ms; }


/* Nav active indicator */
/* bot-ni active: color change only — no underline indicator */

/* Mobile page padding — tighter */
@media (max-width: 680px) {
  .page {
    padding-left: var(--sp4);
    padding-right: var(--sp4);
    padding-bottom: 100px;
  }
}

.mb-0 { margin-bottom: 0 !important; }
.text-xs  { font-size: var(--fs-xs); }
.text-sm  { font-size: var(--fs-sm); }
.text-md  { font-size: var(--fs-md); }
.text-t2  { color: var(--t2); }
.text-t3  { color: var(--t3); }
.text-t4  { color: var(--t4); }
.text-ink { color: var(--ink); }
.text-gold{ color: var(--gold2); }
.text-grn { color: var(--grn); }
.text-red { color: var(--red); }
.mono     { font-family: var(--mono); }
.bold     { font-weight: 700; }
.semibold { font-weight: 600; }
.truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nowrap   { white-space: nowrap; }
.flex     { display: flex; }
.col      { display: flex; flex-direction: column; }
.items-center { align-items: center; }
.justify-between { justify-content: space-between; }
.flex-1   { flex: 1; min-width: 0; }
.shrink-0 { flex-shrink: 0; }
.w-full   { width: 100%; }
.gap-1    { gap: var(--sp1); }
.gap-2    { gap: var(--sp2); }
.gap-3    { gap: var(--sp3); }
.gap-4    { gap: var(--sp4); }
.p-3      { padding: var(--sp3); }
.p-4      { padding: var(--sp4); }
.mt-2     { margin-top: var(--sp2); }
.mt-3     { margin-top: var(--sp3); }
.mt-4     { margin-top: var(--sp4); }
.mb-2     { margin-bottom: var(--sp2); }
.mb-3     { margin-bottom: var(--sp3); }
.mb-4     { margin-bottom: var(--sp4); }


@media (min-width: 680px) {
  .page { padding-left: var(--sp8); padding-right: var(--sp8); }
}
@media (min-width: 1100px) {
  .page { padding-left: var(--sp10); padding-right: var(--sp10); }
}

/* ── INLINE STYLE REPLACEMENTS ── */
/* Row patterns */
.row-sb-xs { display:flex; justify-content:space-between; font-size:var(--fs-sm); }
.row-sb-sp3 { display:flex; align-items:center; justify-content:space-between; margin-bottom:var(--sp3); }
.row-g1 { display:flex; gap:var(--sp1); }
.row-g2 { display:flex; gap:var(--sp3); }
.row-g2-center { display:flex; gap:var(--sp2); align-items:center; }
.row-g2-wrap { display:flex; gap:var(--sp2); align-items:center; flex-wrap:wrap; }
.row-g2-mt4 { display:flex; gap:var(--sp2); margin-top:var(--sp4); }
.row-g3-start { display:flex; gap:var(--sp3); align-items:flex-start; margin-bottom:var(--sp3); }
.row-g3-start-mb4 { display:flex; gap:var(--sp3); align-items:flex-start; margin-bottom:var(--sp4); }
.row-end-g1 { display:flex; gap:var(--sp1); flex-shrink:0; }
.row-end-g1-wrap { display:flex; gap:var(--sp1); flex-shrink:0; flex-wrap:wrap; justify-content:flex-end; }
.col-g2 { display:flex; flex-direction:column; gap:var(--sp2); }
.col-g2-mb3 { display:flex; flex-direction:column; gap:var(--sp2); margin-bottom:var(--sp3); }
/* Grid patterns */
.g2-gap2 { display:grid; grid-template-columns:1fr 1fr; gap:var(--sp2); }
.g2-gap2-mt3 { display:grid; grid-template-columns:1fr 1fr; gap:var(--sp2); margin-top:var(--sp3); }
/* Text patterns — mono metadata */
.meta-xs { font-size:var(--fs-xs); color:var(--t3); font-family:var(--mono); }
.meta-xs-t4 { font-size:var(--fs-xs); color:var(--t4); font-family:var(--mono); }
.meta-xs-grn { font-size:var(--fs-xs); color:var(--grn); font-family:var(--mono); }
.meta-xs-gold { font-size:var(--fs-xs); color:var(--gold); font-family:var(--mono); }
.meta-xs-gold2 { font-size:var(--fs-xs); color:var(--gold2); font-family:var(--mono); }
.meta-xs-mb2 { font-size:var(--fs-xs); color:var(--t3); font-family:var(--mono); margin-bottom:var(--sp2); }
.meta-xs-mt2 { font-size:var(--fs-xs); color:var(--t3); font-family:var(--mono); margin-top:var(--sp2); }
.body-sm { font-size:var(--fs-sm); color:var(--t2); line-height:1.65; }
.body-sm-mb3 { font-size:var(--fs-sm); color:var(--t2); line-height:1.65; margin-bottom:var(--sp3); }
.body-md { font-size:var(--fs-md); font-weight:600; color:var(--ink); margin-bottom:var(--sp1); }
/* Flex children */
.f1-min0 { flex:1; min-width:0; }
.f1-oy-auto { flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch; }
.shrink-0-ml3 { flex-shrink:0; margin-left:var(--sp3); }
/* Spacing */
.mb0 { margin-bottom:0 !important; }
.mb2 { margin-bottom:var(--sp2); }
.mb3 { margin-bottom:var(--sp3); }
.mb4 { margin-bottom:var(--sp4); }
.mt3 { margin-top:var(--sp3); }
.mt4 { margin-top:var(--sp4); }
.pt4-bt1 { padding-top:var(--sp4); border-top:1px solid var(--b1); }
/* Value display patterns */
.val-xl-gold2 { font-size:var(--fs-xl); font-weight:700; color:var(--gold2); letter-spacing:-.5px; line-height:1; }
/* Image thumbnails */
.thumb-44 { width:44px; height:44px; border-radius:var(--r2); object-fit:cover; }
.thumb-34 { width:34px; height:34px; border-radius:var(--r); object-fit:cover; flex-shrink:0; }
/* Section label inline variants */
.section-label-inline { margin-bottom:0; border:none; padding-bottom:var(--sp1); }
/* Divider bar */
.div-bar { height:4px; background:var(--b2); border-radius:2px; overflow:hidden; }
/* Finance row */
.fin-row-sm { display:flex; justify-content:space-between; font-size:var(--fs-sm); }
.fin-val-t2 { color:var(--t2); }
.fin-val-mono { color:var(--ink); font-family:var(--mono); }
.fin-val-grn { color:var(--grn); font-family:var(--mono); }
.fin-val-blu { color:var(--blu); font-family:var(--mono); }
.fin-val-red { color:var(--red); }


/* ── Additional utilities from second pass ── */
.row-g1-center { display:flex; gap:var(--sp1); align-items:center; }
.row-g2-mt4 { display:flex; gap:var(--sp2); margin-top:var(--sp4); }
.mt2 { margin-top:var(--sp2); }
.meta-xs-t2 { font-size:var(--fs-xs); color:var(--t2); font-family:var(--mono); }
.text-t3 { color:var(--t3); }



/* ── UTILITY ALIASES (resolve naming duplication) ── */
.d-flex { display: flex; }
.ai-center { align-items: center; }
.jc-between { justify-content: space-between; }
.jc-end { justify-content: flex-end; }
/* Ensure existing utilities that may conflict are resolved */
.row-g2 { display:flex; gap:var(--sp2); }
.row-g2-center { display:flex; gap:var(--sp2); align-items:center; }



.row-g3 { display:flex; gap:var(--sp3); }
.row-g3-center { display:flex; gap:var(--sp3); align-items:center; }
.col-g3 { display:flex; flex-direction:column; gap:var(--sp3); }
.col-g4 { display:flex; flex-direction:column; gap:var(--sp4); }
.text-ink { color:var(--ink); }


/* ── FINAL UTILITY PASS ── */
.h80 { height: 80px; }
.flex-shrink-col { display:flex; flex-direction:column; gap:var(--sp3); }
.f1 { flex:1; }
.xs-shrink0 { font-size:var(--fs-xs); flex-shrink:0; }
.badge-gold-bg { background:var(--goldbg); color:var(--gold2); }
.badge-blu-bg  { background:var(--blubg);  color:var(--blu); }
.badge-grn-bg  { background:var(--grnbg);  color:var(--grn); }


.mb1 { margin-bottom:var(--sp1); }
.mt1 { margin-top:var(--sp1); }
.mt2 { margin-top:var(--sp2); }

.bt1 { border-bottom:1px solid var(--b1); }

::selection {
  background: rgba(184,115,51,.2);
  color: var(--ink);
}
/* ═══ PULSE CUSTOMIZE MODAL — drag-reorder metric picker ═══ */
.pc-backdrop {
  position: fixed; inset: 0; z-index: 400;
  background: rgba(0,0,0,.55);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  padding: var(--sp4);
  animation: pcFade .15s ease-out;
}
@keyframes pcFade { from { opacity: 0; } to { opacity: 1; } }
.pc-modal {
  background: var(--s1);
  border: 1px solid var(--b1);
  border-radius: var(--r3);
  width: 100%;
  max-width: 420px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 12px 48px rgba(0,0,0,.5);
}
.pc-hdr {
  padding: var(--sp4) var(--sp5) var(--sp3);
  border-bottom: 1px solid var(--b1);
  display: flex; align-items: center; justify-content: space-between;
}
.pc-hdr-title {
  font-family: var(--sans); font-size: var(--fs-md); font-weight: 600;
  color: var(--ink); letter-spacing: -.2px;
}
.pc-hdr-sub {
  font-family: var(--sans); font-size: var(--fs-xs); color: var(--t3);
  margin-top: 2px;
}
.pc-hdr-close {
  background: none; border: none; cursor: pointer;
  color: var(--t3); font-size: 18px; line-height: 1; padding: 4px 8px;
  border-radius: var(--r);
  transition: color .1s, background .1s;
}
.pc-hdr-close:hover { color: var(--ink); background: var(--s2); }
.pc-list {
  padding: var(--sp2) var(--sp3) var(--sp3);
  display: flex; flex-direction: column; gap: 2px;
}
.pc-item {
  display: flex; align-items: center; gap: var(--sp3);
  padding: 10px var(--sp3);
  border-radius: var(--r2);
  cursor: pointer;
  transition: background .1s;
  user-select: none;
  -webkit-user-select: none;
}
.pc-item:hover { background: var(--s2); }
.pc-item.pc-item-on { background: var(--accent-w); }
.pc-item.pc-item-dragging { opacity: .4; }
.pc-item.pc-item-dragover { box-shadow: inset 0 2px 0 0 var(--gold); }
.pc-drag {
  color: var(--t4); font-size: 14px; cursor: grab; flex-shrink: 0;
  font-family: var(--mono); letter-spacing: -1px; line-height: 1;
}
.pc-drag:active { cursor: grabbing; }
.pc-item:not(.pc-item-on) .pc-drag { opacity: 0; pointer-events: none; }
.pc-check {
  width: 16px; height: 16px; border-radius: 4px;
  border: 1.5px solid var(--b3);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: background .1s, border-color .1s;
}
.pc-item-on .pc-check { background: var(--gold); border-color: var(--gold); }
.pc-check-mark {
  width: 8px; height: 8px;
  color: var(--on-gold);
  display: none;
}
.pc-item-on .pc-check-mark { display: block; }
.pc-label {
  flex: 1; font-family: var(--sans); font-size: var(--fs-sm);
  color: var(--t2);
}
.pc-item-on .pc-label { color: var(--ink); }
.pc-meta {
  font-family: var(--mono); font-size: var(--fs-xs);
  color: var(--t4);
}
.pc-footer {
  padding: var(--sp3) var(--sp5);
  border-top: 1px solid var(--b1);
  display: flex; align-items: center; justify-content: space-between;
  background: var(--bg);
}
.pc-count {
  font-family: var(--mono); font-size: var(--fs-xs);
  color: var(--t3); letter-spacing: .04em;
}
.pc-count.pc-count-max { color: var(--gold); }
.pc-done {
  background: var(--gold); color: var(--on-gold);
  border: none; border-radius: var(--r2);
  padding: 8px 18px;
  font-family: var(--sans); font-size: var(--fs-sm); font-weight: 600;
  cursor: pointer;
  transition: background .1s;
}
.pc-done:hover { background: var(--gold2); }
.pc-done:disabled { opacity: .4; cursor: not-allowed; }

.pulse-customize {
  display: flex; justify-content: flex-end;
  margin: calc(var(--sp2) * -1) 0 var(--sp4);
}
.pulse-customize-btn {
  background: none; border: none; cursor: pointer;
  font-family: var(--mono); font-size: var(--fs-xs);
  color: var(--t4); padding: 4px 6px;
  border-radius: var(--r);
  letter-spacing: .04em;
  transition: color .1s, background .1s;
}
.pulse-customize-btn:hover { color: var(--t2); background: var(--s2); }

/* ── iOS input zoom prevention — all pages ── */
@media (max-width: 767px) {
  input, select, textarea { font-size: max(16px, 1em); }
}



/* ═══════════════════════════════════════════════════════════════════
   DESIGN REFINEMENT PASS — Production polish
   ─────────────────────────────────────────────────────────────────
   Principles applied:
   - Subtraction: removed decorative elements, reduced noise
   - Precision: strict 8pt grid, no approximations
   - Consistency: one font family (--sans) for all UI text
   - Hierarchy: weight/color over size/decoration for emphasis
   ═══════════════════════════════════════════════════════════════════ */

/* ── 1. Remove ALL uppercase from UI labels globally ────────────────────── */
/* sb-group-label: sidebar section headers */
.sb-group-label {
  text-transform: none;
  letter-spacing: 0.04em;
  font-family: var(--sans);
  font-size: var(--fs-xs);
  color: var(--t4);
  padding: var(--sp3) 12px var(--sp1);
  font-weight: 400;
}

/* ── 2. Sidebar nav: tighter, more refined ──────────────────────────────── */
.sb-nav { padding: var(--sp2) var(--sp3); }
.ni {
  height: 34px;
  border-radius: var(--r2);
  letter-spacing: 0;
  font-size: var(--fs-sm);
}

/* ── 3. Card hierarchy: three clean levels, no shadows ─────────────────── */
/* Level 1: transparent with hairline border — for inline content areas */
.card-inset {
  background: var(--s1);
  border: 0.5px solid var(--b1);
}
@media (hover: hover) { .card-inset:hover { background: var(--s2); border-color: var(--b2); } }

/* Level 2: card-gold — simpler top accent */
.card-gold {
  border-top: 1.5px solid color-mix(in srgb, var(--accent) 40%, transparent);
  border-left: 0.5px solid var(--b1);
  border-right: 0.5px solid var(--b1);
  border-bottom: 0.5px solid var(--b1);
}

/* ── 4. Divider: even thinner ───────────────────────────────────────────── */
.divider { height: 0.5px; background: var(--b1); margin: var(--sp4) 0; opacity: 0.7; }
.sb-divider { height: 0.5px; background: var(--b1); margin: var(--sp2) var(--sp3); opacity: 0.6; }

/* ── 5. Progress bars: remove over-styled fill ──────────────────────────── */
.prog { height: 3px; background: var(--b1); border-radius: 2px; }
.prog-fill { border-radius: 2px; transition: width .4s cubic-bezier(.4,0,.2,1); }

/* ── 6. Toast: cleaner, less pill-ish ───────────────────────────────────── */
.toast {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  background: var(--ink);
  color: var(--bg);
  font-family: var(--sans);
  font-size: var(--fs-sm);
  font-weight: 400;
  padding: var(--sp2) var(--sp4);
  border-radius: var(--r2);
  z-index: 9999;
  white-space: nowrap;
  letter-spacing: 0;
  opacity: 0.95;
}

/* ── 7. Mobile: bottom nav label weight ────────────────────────────────── */
.bot-ni-lbl { font-weight: 400; letter-spacing: 0; }
.bot-ni.on .bot-ni-lbl { font-weight: 500; }

/* ── 8. Inputs: cleaner focus state ────────────────────────────────────── */
input:focus, select:focus, textarea:focus {
  outline: none;
}
input, select, textarea {
  border-radius: var(--r2);
  transition: border-color var(--t-fast);
}

/* ── 9. Stat number: consistent weight ─────────────────────────────────── */
.stat-n {
  font-size: 22px;
  font-weight: 300;
  letter-spacing: -0.5px;
  line-height: 1;
  margin-bottom: var(--sp1);
}
.stat-l {
  font-size: var(--fs-xs);
  color: var(--t3);
  letter-spacing: 0.03em;
  font-family: var(--sans);
  text-transform: none;
}

/* ── 10. CyclingTitle: consistent with app-wide type scale ─────────────── */
/* Rendered via CyclingTitle primitive — these classes match its output */
.ct-base { font-weight: 300; color: var(--t3); }
.ct-variant { font-weight: 400; color: var(--ink); }

/* ── 11. Mobile page header: reduce top padding ──────────────────────────── */
@media (max-width: 680px) {
  .page { padding-top: var(--sp3); }
  .card, .card-inset, .card-border, .card-gold {
    border-radius: var(--r3);
  }
  /* Tighter stat cells on mobile */
  .stat-cell { padding: var(--sp2) var(--sp3); }
  /* Remove icon scale animations on touch */
  .bot-ni:not(.is-fab).on .bot-ni-ic { transform: none; }
}

/* ── 12. Desktop sidebar: more refined ──────────────────────────────────── */
@media (min-width: 681px) {
  .sb { border-right: 0.5px solid var(--b1); background: var(--bg); }
  .sb-logo { padding: var(--sp5) var(--sp4) var(--sp4); }
}

/* ── 13. Link rows: reduce noise ───────────────────────────────────────── */
.list-row { border-bottom: 0.5px solid var(--b1); }
.task-row { border-bottom: 0.5px solid var(--b1); }

/* ── 14. Chip/pill: one consistent style across app ──────────────────────── */
/* All pills: --sans, 12px, rounded-full, consistent padding */
.chip, .filter-chip, .type-pill {
  font-family: var(--sans);
  font-size: var(--fs-xs);
  border-radius: var(--pill);
  padding: 4px 10px;
  font-weight: 400;
  letter-spacing: 0;
  transition: background var(--t-fast), color var(--t-fast), border-color var(--t-fast);
  -webkit-tap-highlight-color: transparent;
}

/* ── 15. Remove glow effects from focus ring ──────────────────────────────── */
*:focus-visible {
  outline: 1.5px solid color-mix(in srgb, var(--accent) 70%, transparent);
  outline-offset: 2px;
  border-radius: var(--r);
}
`;
